import React, { useEffect, useState, useRef, useCallback } from "react";
import axiosInstance from "../../../httpInterceptor";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  Skeleton,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExcelJS from "exceljs";
import { toast } from "react-toastify";

// API Client utility for optimizing API calls
const APIClient = {

  cache: new Map<string, { data: any, timestamp: number }>(),


  // Extended cache expiration to prevent data from vanishing too soon
  CACHE_EXPIRATION: 8 * 60 * 60 * 1000, // 8 hours instead of 30 minutes

  // Adding a safety check to prevent complete data loss
  STALE_CACHE_FALLBACK: true, // Even if cache expires, keep data as fallback

  pendingRequests: new Map<string, Promise<any>>(),
  generateCacheKey(endpoint: string, params?: any): string {
    return `${endpoint}_${JSON.stringify(params || {})}`;
  },

  isValidCache(entry: { data: any, timestamp: number }): boolean {
    return Date.now() - entry.timestamp < this.CACHE_EXPIRATION;
  },

  async fetch(endpoint: string, options?: any, skipCache: boolean = false): Promise<any> {
    const cacheKey = this.generateCacheKey(endpoint, options);
    if (!skipCache && this.cache.has(cacheKey)) {
      const cacheEntry = this.cache.get(cacheKey)!;
      if (this.isValidCache(cacheEntry)) {
        console.log(`Using cached data for: ${endpoint}`);
        return cacheEntry.data;
      } else {
        // Instead of deleting cached data immediately, keep it as a fallback
        console.log(`Cache expired for: ${endpoint}, but keeping stale data as fallback`);
        // We'll try to get fresh data, but won't delete the cache entry yet
      }
    }
    if (this.pendingRequests.has(cacheKey)) {
      console.log(`Reusing pending request for: ${endpoint}`);
      return this.pendingRequests.get(cacheKey);
    }

    // Store stale data as fallback before making new request
    const staleData = this.STALE_CACHE_FALLBACK && this.cache.has(cacheKey)
      ? this.cache.get(cacheKey)!.data
      : null;

    const requestPromise = (async () => {
      try {
        let response;
        const config = options?.config || {};

        // Ensure authorization token is included
        if (!config.headers) {
          config.headers = {};
        }
        if (!config.headers.Authorization) {
          const token = sessionStorage.getItem("PBT_ID_TOKEN");
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        console.log(`[DEBUG] Executing ${options?.method || 'GET'} request to: ${endpoint}`);
        console.log(`[DEBUG] Request Config:`, config);

        // Always include the content type header
        config.headers["Content-Type"] = "application/json";

        if (options?.body) {
          console.log(`[DEBUG] Request Body:`, typeof options.body, Array.isArray(options.body) ?
            `Array with ${options.body.length} items` :
            JSON.stringify(options.body).substring(0, 100));
        }

        if (options?.method === 'GET' || !options?.method) {
          console.log('[DEBUG] Making GET request with config:', config);
          response = await axiosInstance.get(endpoint, config);
        } else {
          console.log('[DEBUG] Making POST request with config:', config);
          response = await axiosInstance.post(endpoint, options?.body || {}, config);
        }

        const data = response.data;
        console.log(`[DEBUG] API response received for ${endpoint}:`,
          typeof data,
          Array.isArray(data) ? `Array with ${data.length} items` :
            (typeof data === 'object' ? `Object with ${Object.keys(data).length} keys` :
              JSON.stringify(data).substring(0, 100))
        );

        this.cache.set(cacheKey, { data, timestamp: Date.now() });

        return data;
      } catch (error) {
        console.error(`Error fetching ${endpoint}:`, error);

        // If we have stale data and fallback is enabled, return it instead of throwing
        if (this.STALE_CACHE_FALLBACK && staleData) {
          console.log(`Using stale data as fallback for: ${endpoint}`);
          return staleData;
        }

        throw error;
      } finally {
        this.pendingRequests.delete(cacheKey);
      }
    })();

    this.pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  },

  // Method to batch multiple API calls together with improved error handling
  async batchFetch(requests: Array<{ endpoint: string, options?: any }>): Promise<any[]> {
    try {
      console.log(`[DEBUG] Executing batch fetch with ${requests.length} requests`);

      // Add individual try/catch for each request to prevent one failure from breaking all requests
      const results = await Promise.all(
        requests.map(async (request, index) => {
          try {
            console.log(`[DEBUG] Starting request ${index + 1} to ${request.endpoint}`);
            const result = await this.fetch(request.endpoint, request.options);
            console.log(`[DEBUG] Completed request ${index + 1} successfully`);
            return result;
          } catch (error) {
            console.error(`[DEBUG] Request ${index + 1} to ${request.endpoint} failed:`, error);
            // Return a safe default value instead of letting the error propagate
            return request.options?.defaultValue || null;
          }
        })
      );

      console.log(`[DEBUG] Batch fetch completed with ${results.length} results`);
      return results;
    } catch (error) {
      console.error('[DEBUG] Critical error in batchFetch:', error);
      return requests.map(() => null); // Return array of nulls on critical failure
    }
  },

  // Clear all cached data
  clearCache(): void {
    this.cache.clear();
  },

  // Clear specific cache entry
  clearCacheEntry(endpoint: string, params?: any): void {
    const cacheKey = this.generateCacheKey(endpoint, params);
    this.cache.delete(cacheKey);
  }
};

// Removed global flag for fetchData deduplication

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

type StatusKey = "Open" | "Close" | "In Progress";
type AssessmentStatus = "Red" | "Amber" | "Green";

interface CompassProjectData {
  projectName: string;
  projectid: string;
  duname: string;
  puname: string;
  accountname: string;
  created_date: string;
  end_date: string;
}

const quarterMonthMap: { [key: string]: string[] } = {
  Q1: ["January", "February", "March"],
  Q2: ["April", "May", "June"],
  Q3: ["July", "August", "September"],
  Q4: ["October", "November", "December"],
};

const getQuarter = (dateStr: string): string => {
  const date = new Date(dateStr);
  const quarter = Math.floor(date.getMonth() / 3) + 1;
  return `${date.getFullYear()}-Q${quarter}`;
};

// This function is not currently used but may be needed in future updates
// for dynamic quarter selection
/*
const getLatest4Quarters = (): string[] => {
  const now = new Date();
  const quarters: string[] = [];
  for (let i = 0; i < 4; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    quarters.push(`${date.getFullYear()}-Q${quarter}`);
  }
  return quarters.sort();
};
*/

// Helper function to normalize status values (case insensitive)
const normalizeStatus = (status: string | undefined): string | undefined => {
  if (!status) return undefined;
  const normalized = status.toLowerCase();
  if (normalized === 'red') return 'Red';
  if (normalized === 'amber') return 'Amber';
  if (normalized === 'green') return 'Green';
  if (normalized === 'n/a' || normalized === 'N/A') return 'N/A';
  if (normalized === 'open') return 'Open';
  if (normalized === 'closed' || normalized === 'close') return 'Close';
  if (normalized === 'in progress') return 'In Progress';
  return status; // Return original if not matching
};

// Chart skeleton loading component
const ChartSkeletonLoader = ({ height = 300 }: { height?: number }) => {
  return (
    <Box sx={{
      width: '100%',
      height: `${height}px`,
      position: 'relative',
      animation: 'pulse 1.5s infinite ease-in-out',
      '@keyframes pulse': {
        '0%': {
          opacity: 0.7,
        },
        '50%': {
          opacity: 0.9,
        },
        '100%': {
          opacity: 0.7,
        },
      }
    }}>
      {/* Chart title skeleton */}
      <Skeleton variant="text" sx={{ width: '50%', height: 40, mx: 'auto', mb: 2 }} />

      {/* Legend skeleton */}
      <Box sx={{ position: 'absolute', right: 20, top: 20, display: 'flex', flexDirection: 'column' }}>
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} variant="text" width={80} sx={{ mb: 1 }} />
        ))}
      </Box>

      {/* X-axis labels skeleton */}
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', display: 'flex', justifyContent: 'space-between', px: 5 }}>
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Skeleton key={i} variant="text" width={30} />
        ))}
      </Box>

      {/* Y-axis labels skeleton */}
      <Box sx={{ position: 'absolute', left: 0, top: 20, height: '80%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} variant="text" width={20} />
        ))}
      </Box>

      {/* Chart bars skeleton */}
      <Box sx={{ mt: 5, mx: 5, display: 'flex', height: '60%', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '12%' }}>
            {[1, 2, 3, 4].map(j => (
              <Skeleton
                key={j}
                variant="rectangular"
                width="100%"
                height={`${20 * Math.random() * j}px`}
                sx={{
                  mb: 0,
                  opacity: 0.7,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    opacity: 0.9,
                  },
                  backgroundColor: j === 1 ? '#E74C3C' :
                    j === 2 ? '#F39C12' :
                      j === 3 ? '#27AE60' :
                        '#B2B2B2'
                }}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const statusColors: Record<string, string> = {
  Red: "#E74C3C",
  Amber: "#F39C12",
  Green: "#27AE60",
  "N/A": "#B2B2B2", // Gray for N/A
  Open: "#3498DB",
  Close: "#2ECC71",
  "In Progress": "#F1C40F",
};

const formatMonthYear = (month: string, year: number): string => {
  const monthIndex = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ].indexOf(month);
  if (monthIndex === -1) return `${month}-${year}`;
  const monthAbbr = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ][monthIndex];
  return `${monthAbbr}-${String(year).slice(-2)}`;
};

const ChartExportMenu = ({
  chartRef,
  chartData,
  chartTitle,
  filterInfo,
  loading = false,
}: any) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (e: any) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const exportToPDF = async () => {
    handleClose();
    if (chartData && chartRef.current) {
      const visibleLabels = chartData.labels;
      const visibleDatasets = chartData.datasets;
      const pdf = new jsPDF("landscape", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      pdf.setFillColor(255, 94, 79);
      pdf.rect(0, 0, pageWidth, 25, "F"); // Increased height of header bar to accommodate text better
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont("helvetica", "bold");
      pdf.text(chartTitle || "Chart Data", pageWidth / 2, 18, { // Moved down from 15 to 18
        align: "center",
      });
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "normal");
      pdf.text(
        `Generated on: ${new Date().toLocaleDateString()}`,
        pageWidth / 2,
        24, // Moved down from 22 to 24
        { align: "center" }
      );
      pdf.setTextColor(0, 0, 0);
      let currentY = 35; // Adjusted from 32 to 35 to account for the taller header
      pdf.setFontSize(13);
      pdf.text("Applied Filters:", margin, currentY);
      currentY += 7;
      if (filterInfo) {
        const filterTexts = Object.entries(filterInfo)
          .filter(([_, v]) => v)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`);
        const filterLine = filterTexts.join(" | ");
        const maxLineLength = 100;
        pdf.setFontSize(10);
        if (filterLine.length <= maxLineLength) {
          pdf.text(filterLine, margin, currentY);
          currentY += 7;
        } else {
          let currentLine = "";
          let currentLen = 0;
          filterTexts.forEach((text, i) => {
            if (
              currentLen + text.length + 3 > maxLineLength &&
              currentLen > 0
            ) {
              pdf.text(currentLine, margin, currentY);
              currentY += 5;
              currentLine = text;
              currentLen = text.length;
            } else {
              currentLine += (currentLen > 0 ? " | " : "") + text;
              currentLen += text.length + (currentLen > 0 ? 3 : 0);
            }
            if (i === filterTexts.length - 1) {
              pdf.text(currentLine, margin, currentY);
              currentY += 7;
            }
          });
        }
      }
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text("Data Graph:", margin, currentY);
      currentY += 8;
      try {
        const canvas = await html2canvas(chartRef.current);
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = (pageWidth - 2 * margin) * 0.75;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const maxHeight = pageHeight - currentY - margin * 2;
        const finalHeight = Math.min(imgHeight, maxHeight);
        pdf.addImage(
          imgData,
          "PNG",
          margin + (pageWidth - 2 * margin - imgWidth) / 2,
          currentY,
          imgWidth,
          finalHeight
        );
        pdf.addPage();
        currentY = 20;
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");

        // Get custom title based on chart type
        let customTitle = "Data Table:";
        if (chartTitle === "Monthly Assessment Status") {
          customTitle = "Count of Assessments by Month (Mmm-YY):";
        } else if (chartTitle === "Findings by Status per Month") {
          customTitle = "Count of Findings by Month (Mmm-YY):";
        } else if (chartTitle === "Quarterly Assessment Status vs Findings Status") {
          customTitle = "Count Quarterly Assessment Status vs Findings Status by Month (Mmm-YY):";
        } else if (chartTitle === "Assessment Report") {
          customTitle = "Count of Assessment Report Status by Month (Mmm-YY):";
        }

        pdf.text(customTitle, margin, currentY);
        currentY += 8;
        pdf.setFont("helvetica", "normal");
        const headers = [
          "Category",
          ...visibleDatasets.map((ds: any) => ds.label),
          "Total" // Add Total column to headers
        ];
        const tableData = visibleLabels.map((label: any, i: number) => {
          // Get data for current row
          const rowData = visibleDatasets.map((ds: any) => ds.data[i]);
          // Calculate total by summing all numeric values in the row
          const rowTotal = rowData.reduce((sum: number, val: any) => {
            return sum + (typeof val === 'number' ? val : 0);
          }, 0);
          return [
            label,
            ...rowData,
            rowTotal, // Add total to row data
          ];
        });
        // Adjust font size and column width based on chart type
        let fontSize = 12;
        if (chartTitle === "Quarterly Assessment Status vs Findings Status") {
          // Use smaller font size for the chart with many columns
          fontSize = 9; // Increased from 7 to 9 for better readability
        }

        const colWidth = (pageWidth - 2 * margin) / headers.length;
        pdf.setFillColor(255, 94, 79);
        pdf.rect(margin, currentY, pageWidth - 2 * margin, 10, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(fontSize);

        // For Quarterly Assessment Status vs Findings Status, increase row height
        let headerRowHeight = 10;
        if (chartTitle === "Quarterly Assessment Status vs Findings Status") {
          headerRowHeight = 18; // Increased from 15 to 18 for better readability with larger font
        }

        pdf.rect(margin, currentY, pageWidth - 2 * margin, headerRowHeight, "F");

        // Draw each header text
        headers.forEach((h, i) => {
          // For Quarterly Assessment, add text wrapping for long headers
          if (chartTitle === "Quarterly Assessment Status vs Findings Status" && h.includes(" ")) {
            const words = h.split(" ");
            const firstLine = words.slice(0, -1).join(" ");
            const secondLine = words[words.length - 1];

            pdf.text(firstLine, margin + i * colWidth + colWidth / 2, currentY + 5, {
              align: "center",
            });
            pdf.text(secondLine, margin + i * colWidth + colWidth / 2, currentY + 12, {
              align: "center",
            });
          } else {
            pdf.text(h, margin + i * colWidth + colWidth / 2, currentY + (headerRowHeight / 2) + 1, {
              align: "center",
            });
          }
        });
        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(fontSize);

        // Adjust currentY based on header row height
        currentY += headerRowHeight;

        // Set data row height (same as header if using Quarterly Assessment chart)
        let dataRowHeight = 10;
        if (chartTitle === "Quarterly Assessment Status vs Findings Status") {
          dataRowHeight = 14; // Increased from 12 to 14 for better readability with larger font
        }

        tableData.forEach((row: any, rowIndex: number) => {
          if (rowIndex % 2 === 0) {
            pdf.setFillColor(240, 240, 240);
          } else {
            pdf.setFillColor(255, 255, 255);
          }
          pdf.rect(margin, currentY, pageWidth - 2 * margin, dataRowHeight, "F");
          row.forEach((cell: any, cellIndex: number) => {
            const cellValue = typeof cell === "number" ? cell.toString() : cell;

            // Apply color formatting based on chart type and column
            if (chartTitle === "Monthly Assessment Status") {
              if (headers[cellIndex] === "Red") {
                pdf.setTextColor(255, 0, 0); // Red
              } else if (headers[cellIndex] === "Amber") {
                pdf.setTextColor(255, 191, 0); // Amber
              } else if (headers[cellIndex] === "Green") {
                pdf.setTextColor(0, 128, 0); // Green
              } else if (headers[cellIndex] === "NoRAG" || headers[cellIndex] === "N/A") {
                pdf.setTextColor(105, 105, 105); // Deep Ash
              } else {
                pdf.setTextColor(0, 0, 0); // Black (default)
              }
            } else if (chartTitle === "Findings by Status per Month") {
              if (headers[cellIndex] === "Open") {
                pdf.setTextColor(0, 120, 212); // Light Blue
              } else if (headers[cellIndex] === "In Progress") {
                pdf.setTextColor(255, 191, 0); // Yellow
              } else if (headers[cellIndex] === "Close") {
                pdf.setTextColor(118, 195, 65); // Light Green
              } else {
                pdf.setTextColor(0, 0, 0); // Black (default)
              }
            } else if (chartTitle === "Quarterly Assessment Status vs Findings Status") {
              if (headers[cellIndex] === "Assessment Red") {
                pdf.setTextColor(255, 0, 0); // Red
              } else if (headers[cellIndex] === "Assessment Amber") {
                pdf.setTextColor(255, 191, 0); // Amber
              } else if (headers[cellIndex] === "Assessment Green") {
                pdf.setTextColor(0, 128, 0); // Green
              } else if (headers[cellIndex] === "Assessment NoRAG") {
                pdf.setTextColor(105, 105, 105); // Deep Ash
              } else if (headers[cellIndex] === "Findings Open") {
                pdf.setTextColor(0, 120, 212); // Light Blue
              } else if (headers[cellIndex] === "Findings In Progress") {
                pdf.setTextColor(255, 191, 0); // Yellow
              } else if (headers[cellIndex] === "Findings Close") {
                pdf.setTextColor(118, 195, 65); // Light Green
              } else {
                pdf.setTextColor(0, 0, 0); // Black (default)
              }
            } else if (chartTitle === "Assessment Report") {
              if (headers[cellIndex] === "Completed") {
                pdf.setTextColor(0, 128, 0); // Green
              } else if (headers[cellIndex] === "Submitted") {
                pdf.setTextColor(255, 191, 0); // Orange
              } else if (headers[cellIndex] === "Draft") {
                pdf.setTextColor(255, 0, 0); // Red
              } else {
                pdf.setTextColor(0, 0, 0); // Black (default)
              }
            } else {
              pdf.setTextColor(0, 0, 0); // Default black for other chart types
            }

            pdf.text(
              cellValue,
              margin + cellIndex * colWidth + colWidth / 2,
              currentY + (dataRowHeight / 2) + 1,
              { align: "center" }
            );

            // Reset text color to black after drawing the text
            pdf.setTextColor(0, 0, 0);
          });
          currentY += dataRowHeight;
        });
        pdf.save(
          `${chartTitle || "chart"}_${new Date().toISOString().split("T")[0]
          }.pdf`
        );
      } catch (err) {
        console.error("Error capturing chart image:", err);
        alert("PDF generation failed.");
      }
    }
  };

  const exportToExcel = async () => {
    handleClose();
    if (chartData && chartRef.current) {
      try {
        const canvas = await html2canvas(chartRef.current);
        const imgData = canvas.toDataURL("image/png");
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Chart Data");
        worksheet.addRow(["Chart:", chartTitle || "Chart Data"]);
        worksheet.addRow(["Generated on:", new Date().toLocaleDateString()]);
        worksheet.addRow([]);
        if (filterInfo) {
          worksheet.addRow(["Applied Filters:"]);
          Object.entries(filterInfo)
            .filter(([_, v]) => v)
            .forEach(([k, v]) => {
              worksheet.addRow([
                k + ":",
                Array.isArray(v) ? v.join(", ") : String(v),
              ]);
            });
        }
        worksheet.addRow([]);

        // Get custom title based on chart type
        let customTitle = "Data Table:";
        if (chartTitle === "Monthly Assessment Status") {
          customTitle = "Count of Assessments by Month (Mmm-YY):";
        } else if (chartTitle === "Findings by Status per Month") {
          customTitle = "Count of Findings by Month (Mmm-YY):";
        } else if (chartTitle === "Quarterly Assessment Status vs Findings Status") {
          customTitle = "Count Quarterly Assessment Status vs Findings Status by Month (Mmm-YY):";
        } else if (chartTitle === "Assessment Report") {
          customTitle = "Count of Assessment Report Status by Month (Mmm-YY):";
        }

        worksheet.addRow([customTitle]);
        const visibleLabels = chartData.labels;
        const visibleDatasets = chartData.datasets;
        worksheet.addRow([
          "Category",
          ...visibleDatasets.map((ds: any) => ds.label),
          "Total" // Add Total column to headers
        ]);
        visibleLabels.forEach((label: any, i: number) => {
          // Get data for current row
          const rowData = visibleDatasets.map((ds: any) => ds.data[i]);
          // Calculate total by summing all numeric values in the row
          const rowTotal = rowData.reduce((sum: number, val: any) => {
            return sum + (typeof val === 'number' ? val : 0);
          }, 0);
          worksheet.addRow([
            label,
            ...rowData,
            rowTotal, // Add total to row data
          ]);
        });
        const headerRowIndex = worksheet.rowCount - visibleLabels.length;
        const headerRow = worksheet.getRow(headerRowIndex);

        // Get column indexes for specific labels based on chart type
        const columnIndexes: Record<string, number> = {};
        visibleDatasets.forEach((ds: any, index: any) => {
          if (ds.label) {
            columnIndexes[ds.label] = index + 2; // +2 because Excel is 1-indexed and first column is Category
          }
        });

        // Style the header row
        headerRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF5E4F" },
          };
          cell.font = { color: { argb: "FFFFFF" }, bold: true };
        });

        // Apply color formatting to each data cell based on chart type and column
        for (let i = 0; i < visibleLabels.length; i++) {
          const dataRowIndex = headerRowIndex + i + 1;
          const dataRow = worksheet.getRow(dataRowIndex);

          if (chartTitle === "Monthly Assessment Status") {
            // Apply text color for Monthly Assessment Status columns
            if (columnIndexes['Red']) {
              const cell = dataRow.getCell(columnIndexes['Red']);
              cell.font = { color: { argb: "FF0000" } }; // Red
            }
            if (columnIndexes['Amber']) {
              const cell = dataRow.getCell(columnIndexes['Amber']);
              cell.font = { color: { argb: "FFBF00" } }; // Amber
            }
            if (columnIndexes['Green']) {
              const cell = dataRow.getCell(columnIndexes['Green']);
              cell.font = { color: { argb: "008000" } }; // Green
            }
            if (columnIndexes['NoRAG'] || columnIndexes['N/A']) {
              const idx = columnIndexes['NoRAG'] || columnIndexes['N/A'];
              if (idx) {
                const cell = dataRow.getCell(idx);
                cell.font = { color: { argb: "696969" } }; // Deep Ash
              }
            }
          } else if (chartTitle === "Findings by Status per Month") {
            // Apply text color for Findings by Status per Month columns
            if (columnIndexes['Open']) {
              const cell = dataRow.getCell(columnIndexes['Open']);
              cell.font = { color: { argb: "0078D4" } }; // Light Blue
            }
            if (columnIndexes['In Progress']) {
              const cell = dataRow.getCell(columnIndexes['In Progress']);
              cell.font = { color: { argb: "FFBF00" } }; // Yellow
            }
            if (columnIndexes['Close']) {
              const cell = dataRow.getCell(columnIndexes['Close']);
              cell.font = { color: { argb: "76C341" } }; // Light Green
            }
          } else if (chartTitle === "Quarterly Assessment Status vs Findings Status") {
            // Apply text color for Quarterly Assessment Status vs Findings Status columns
            if (columnIndexes['Assessment Red']) {
              const cell = dataRow.getCell(columnIndexes['Assessment Red']);
              cell.font = { color: { argb: "FF0000" } }; // Red
            }
            if (columnIndexes['Assessment Amber']) {
              const cell = dataRow.getCell(columnIndexes['Assessment Amber']);
              cell.font = { color: { argb: "FFBF00" } }; // Amber
            }
            if (columnIndexes['Assessment Green']) {
              const cell = dataRow.getCell(columnIndexes['Assessment Green']);
              cell.font = { color: { argb: "008000" } }; // Green
            }
            if (columnIndexes['Assessment NoRAG']) {
              const cell = dataRow.getCell(columnIndexes['Assessment NoRAG']);
              cell.font = { color: { argb: "696969" } }; // Deep Ash
            }
            if (columnIndexes['Findings Open']) {
              const cell = dataRow.getCell(columnIndexes['Findings Open']);
              cell.font = { color: { argb: "0078D4" } }; // Light Blue
            }
            if (columnIndexes['Findings In Progress']) {
              const cell = dataRow.getCell(columnIndexes['Findings In Progress']);
              cell.font = { color: { argb: "FFBF00" } }; // Yellow
            }
            if (columnIndexes['Findings Close']) {
              const cell = dataRow.getCell(columnIndexes['Findings Close']);
              cell.font = { color: { argb: "76C341" } }; // Light Green
            }
          } else if (chartTitle === "Assessment Report") {
            // Apply text color for Assessment Report columns
            if (columnIndexes['Completed']) {
              const cell = dataRow.getCell(columnIndexes['Completed']);
              cell.font = { color: { argb: "008000" } }; // Green
            }
            if (columnIndexes['Submitted']) {
              const cell = dataRow.getCell(columnIndexes['Submitted']);
              cell.font = { color: { argb: "FFBF00" } }; // Orange
            }
            if (columnIndexes['Draft']) {
              const cell = dataRow.getCell(columnIndexes['Draft']);
              cell.font = { color: { argb: "FF0000" } }; // Red
            }
          }
        }
        const imageSheet = workbook.addWorksheet("Chart");
        imageSheet.addRow(["Chart Image:"]);
        imageSheet.getRow(1).font = { bold: true };
        imageSheet.addRow([]);
        const imageId = workbook.addImage({
          base64: imgData,
          extension: "png",
        });
        imageSheet.addImage(imageId, {
          tl: { col: 1, row: 2 },
          ext: { width: 800, height: 400 },
        });
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(
          blob,
          `${chartTitle || "chart"}_data_${new Date().toISOString().split("T")[0]
          }.xlsx`
        );
      } catch (err) {
        console.error("Error exporting to Excel:", err);
        alert("Excel export failed.");
      }
    }
  };

  return (
    <div
      style={{ position: "absolute", top: "10px", right: "10px", zIndex: 10 }}
    >
      <Tooltip title="Export Chart">
        <IconButton onClick={handleClick} size="small" disabled={loading}>
          <FileDownloadIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={exportToPDF} disabled={loading}>Export as PDF</MenuItem>
        <MenuItem onClick={exportToExcel} disabled={loading}>Export as Excel</MenuItem>
      </Menu>
    </div>
  );
};

const QuarterReportCharts = () => {
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number[]>([
    new Date().getFullYear()
  ]);
  const [allYearsSelected, setAllYearsSelected] = useState(true);
  const [selectedQuarters, setSelectedQuarters] = useState<string[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedDU, setSelectedDU] = useState<string[]>(["All"]);
  const [selectedAccount, setSelectedAccount] = useState<string[]>(["All"]);
  const [selectedPU, setSelectedPU] = useState<string[]>(["All"]);

  const [duOptions, setDUOptions] = useState<string[]>([]);
  const [accountOptions, setAccountOptions] = useState<string[]>([]);
  const [puOptions, setPUOptions] = useState<string[]>([]);

  const [compassData, setCompassData] = useState<CompassProjectData[]>([]);
  const [allQuartersSelected, setAllQuartersSelected] = useState(true);
  const [allMonthsSelected, setAllMonthsSelected] = useState(true);

  // Chart-specific loading states
  const [monthlyChartLoading, setMonthlyChartLoading] = useState(true);
  const [findingsChartLoading, setFindingsChartLoading] = useState(true);
  const [quarterlyChartLoading, setQuarterlyChartLoading] = useState(true);
  const [exportAllLoading, setExportAllLoading] = useState(false);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<null | HTMLElement>(null);

  // These state variables are set in the data fetching function but used by the chart components
  // They appear as unused due to conditional rendering but are required for chart data
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [findingsByStatus, setFindingsByStatus] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [assessmentsByQuarter, setAssessmentsByQuarter] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalFindingsByQuarter, setTotalFindingsByQuarter] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [assessmentStatusByQuarter, setAssessmentStatusByQuarter] =
    useState<any>({});
  const [monthlyAssessmentStatus, setMonthlyAssessmentStatus] = useState<any>(
    {}
  );
  const [monthlyObservationStatus, setMonthlyObservationStatus] = useState<any>(
    {}
  );
  const [assessmentReportData, setAssessmentReportData] = useState<any>({});
  const [filteredProjects, setFilteredProjects] = useState<string[]>([]);

  // Initialize state with data from localStorage if available
  const [quarterlyAssessmentStatus, setQuarterlyAssessmentStatus] = useState<any>(() => {
    try {
      const savedData = localStorage.getItem('pbt_quarterly_assessment_status');
      return savedData ? JSON.parse(savedData) : {};
    } catch (err) {
      console.error('Error loading cached quarterly assessment data:', err);
      return {};
    }
  });

  const [quarterlyObservationStatus, setQuarterlyObservationStatus] = useState<any>(() => {
    try {
      const savedData = localStorage.getItem('pbt_quarterly_observation_status');
      return savedData ? JSON.parse(savedData) : {};
    } catch (err) {
      console.error('Error loading cached quarterly observation data:', err);
      return {};
    }
  });

  const [projectIdToSetNameMap, setProjectIdToSetNameMap] = useState<
    Record<string, string>
  >({});
  // Add a guard to prevent duplicate API calls on mount
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Track API request IDs to prevent duplicate calls
  const requestIdRef = useRef({
    quarterly: "",
    monthly: "",
    consolidated: "",
    healthObservations: ""  // Added for the consolidated health and observations calls
  });

  // Month-related constants for date formatting and lookups
  const monthsOrder = React.useMemo(() => [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ], []);

  const monthAbbr = React.useMemo(() => [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
  ], []);

  // Helper function for finding month keys in the observation data
  const findMonthKey = useCallback((
    data: Record<string, any>,
    month: string,
    year: number
  ) => {
    if (!data || Object.keys(data).length === 0) return undefined;
    const standardizedMonth =
      month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
    const monthIndex = monthsOrder.indexOf(month);
    if (monthIndex === -1) return undefined;
    const abbr = monthAbbr[monthIndex];
    const yearShort = String(year).slice(-2);
    const standardKey = `${standardizedMonth} ${year}`;
    if (data[standardKey]) return standardKey;
    const commaKey = `${standardizedMonth}, ${year}`;
    if (data[commaKey]) return commaKey;
    const abbrFormats = [
      `${abbr}-${yearShort}`,
      `${abbr.toLowerCase()}-${yearShort}`,
      `${abbr.charAt(0) + abbr.slice(1).toLowerCase()}-${yearShort}`,
    ];
    for (const f of abbrFormats) {
      if (data[f]) return f;
    }
    const keys = Object.keys(data);
    const lowerMonth = standardizedMonth.toLowerCase();
    const lowerAbbr = abbr.toLowerCase();
    const yearString = year.toString();
    return keys.find((k) => {
      const kLower = k.toLowerCase();
      return (
        (kLower.includes(lowerMonth) || kLower.includes(lowerAbbr)) &&
        (kLower.includes(yearString) || kLower.includes(yearShort))
      );
    });
  }, [monthsOrder, monthAbbr]);

  // Generate a unique request ID based on current filters
  const getQuarterlyRequestId = useCallback(() => {
    // Sort years to ensure consistent request ID regardless of selection order
    const sortedYears = [...selectedYear].sort((a, b) => a - b);
    const requestId = `quarterly_${sortedYears.join('_')}_${selectedDU.sort().join('_')}_${selectedAccount.sort().join('_')}_${selectedPU.sort().join('_')}_${selectedQuarters.sort().join('_')}_${selectedMonths.sort().join('_')}`;
    return requestId;
  }, [selectedYear, selectedDU, selectedAccount, selectedPU, selectedQuarters, selectedMonths]);

  const getMonthlyRequestId = useCallback(() => {
    return `monthly_${selectedYear.join('_')}_${selectedDU}_${selectedAccount || "all"}_${selectedPU || "all"}_${selectedQuarters.join('_')}_${selectedMonths.join('_')}`;
  }, [selectedYear, selectedDU, selectedAccount, selectedPU, selectedQuarters, selectedMonths]);

  // Helper to fetch all projects via paginated /projects endpoint
  const fetchAllProjects = async () => {
    const allProjects: any[] = [];
    let lastKey: string | null = null;

    while (true) {
      const params: string[] = ["limit=30"];
      if (lastKey) {
        params.push(`lastKey=${encodeURIComponent(lastKey)}`);
      }
      const query = params.length ? `?${params.join("&")}` : "";

      const projectsRes = await axiosInstance.get(
        `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/projects${query}`
      );

      // Backward compatibility: old API may return full array directly
      if (Array.isArray(projectsRes.data)) {
        return projectsRes.data;
      }

      const items =
        projectsRes.data && Array.isArray(projectsRes.data.items)
          ? projectsRes.data.items
          : [];
      allProjects.push(...items);

      const nextKey: string | null =
        projectsRes.data && typeof projectsRes.data.nextKey === "string" && projectsRes.data.nextKey.length > 0
          ? projectsRes.data.nextKey
          : null;

      if (!nextKey) break;
      lastKey = nextKey;
    }

    return allProjects;
  };

  // Using getLatest4Quarters function directly when needed instead of storing in a variable

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const projects = await fetchAllProjects();
        if (!projects || !Array.isArray(projects) || projects.length === 0) {
          console.error("Invalid or empty projects data format");
          return;
        }
        const idToSetNameMap: Record<string, string> = {};
        projects.forEach((p: any) => {
          idToSetNameMap[p.projectid] = p.set_name;
        });
        setProjectIdToSetNameMap(idToSetNameMap);
        const projectIds = projects.map((p: any) => p.projectid);
        const compassResponse = await axiosInstance.get(
          `${process.env.REACT_APP_COMPASS_BOOMI_URL
          }?Projectid=${projectIds.join(",")}`,
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": `${process.env.REACT_APP_COMPASS_X_API_KEY}`,
            },
          }
        );
        if (
          !compassResponse.data ||
          !compassResponse.data.result ||
          !Array.isArray(compassResponse.data.result)
        ) {
          console.error("Invalid Compass data format:", compassResponse.data);
          return;
        }
        const compassProjectData = projects;
        setCompassData(compassProjectData);
        const dus = Array.from(
          new Set(compassProjectData.map((p: any) => String(p.duname)))
        )
          .filter(Boolean)
          .sort() as string[];
        setDUOptions(["All", ...dus]);
        const accounts = Array.from(
          new Set(compassProjectData.map((p: any) => String(p.accountname)))
        )
          .filter(Boolean)
          .sort() as string[];
        setAccountOptions(["All", ...accounts]);
        const pus = Array.from(
          new Set(compassProjectData.map((p: any) => p.puname))
        )
          .filter(Boolean)
          .sort() as string[];
        setPUOptions(["All", ...pus]);
        const years = new Set<number>();
        compassProjectData.forEach((p: any) => {
          if (p.created_date) years.add(new Date(p.created_date).getFullYear());
          // if (p.end_date) years.add(new Date(p.end_date).getFullYear());
        });
        const currentYear = new Date().getFullYear();
        years.add(currentYear);
        const yearsArray = Array.from(years).sort((a, b) => b - a);
        setAvailableYears(yearsArray);
        // Select the latest two years by default (yearsArray is sorted desc)
        const defaultSelected = yearsArray.slice(0, 2);
        setSelectedYear(defaultSelected);
        // Only set allYearsSelected true if all available years are selected
        setAllYearsSelected(defaultSelected.length === yearsArray.length);
        setFilteredProjects(projectIds);
      } catch (err) {
        console.error("Error fetching initial data:", err);
      } finally {
        // Mark initial data loaded, even on partial failure
        setInitialDataLoaded(true);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!compassData.length) return;
    if (selectedDU.includes("All")) {
      const allAccounts = Array.from(
        new Set(compassData.map((p) => p.accountname))
      )
        .filter(Boolean)
        .sort();
      setAccountOptions(["All", ...allAccounts]);
    } else {
      const duAccounts = Array.from(
        new Set(
          compassData
            .filter((p) => selectedDU.includes(p.duname))
            .map((p) => p.accountname)
        )
      )
        .filter(Boolean)
        .sort();
      setAccountOptions(["All", ...duAccounts]);
    }
    setSelectedAccount(["All"]);
    setSelectedPU(["All"]);
  }, [selectedDU, compassData]);

  useEffect(() => {
    if (!compassData.length) return;
    if (selectedAccount.includes("All")) {
      if (!selectedDU.includes("All")) {
        const duPUs = Array.from(
          new Set(
            compassData
              .filter((p) => selectedDU.includes(p.duname))
              .map((p) => p.puname)
          )
        )
          .filter(Boolean)
          .sort();
        setPUOptions(["All", ...duPUs]);
      } else {
        const allPUs = Array.from(new Set(compassData.map((p) => p.puname)))
          .filter(Boolean)
          .sort();
        setPUOptions(["All", ...allPUs]);
      }
    } else {
      const filtered = compassData.filter((p) => {
        const duMatch = selectedDU.includes("All") || selectedDU.includes(p.duname);
        const accountMatch = selectedAccount.includes("All") || selectedAccount.includes(p.accountname);
        return duMatch && accountMatch;
      });
      const accountPUs = Array.from(new Set(filtered.map((p) => p.puname)))
        .filter(Boolean)
        .sort();
      setPUOptions(["All", ...accountPUs]);
    }
    setSelectedPU(["All"]);
  }, [selectedAccount, selectedDU, compassData]);

  useEffect(() => {
    if (!compassData.length) return;

    let newFiltered;

    // Special case: When "All" is selected for all filters, include all projects
    if (selectedDU.includes("All") && selectedAccount.includes("All") && selectedPU.includes("All")) {
      console.log("ALL FILTERS ARE 'ALL' - USING ALL PROJECTS");
      newFiltered = compassData.map(p => p.projectid);
    } else {
      // Apply cascading filters for specific selections
      newFiltered = compassData.filter((p) => {
        // Start with DU filter - if "All" is selected, include all DUs
        const duMatch = selectedDU.includes("All") || selectedDU.includes(p.duname);

        // Only apply account filter if a specific account is selected (not "All")
        let accountMatch = true;
        if (!selectedAccount.includes("All")) {
          accountMatch = selectedAccount.includes(p.accountname);
        }

        // Only apply PU filter if a specific PU is selected (not "All")
        let puMatch = true;
        if (!selectedPU.includes("All")) {
          puMatch = selectedPU.includes(p.puname);
        }

        return duMatch && accountMatch && puMatch;
      }).map((p) => p.projectid);
    }

    console.log(`Filtered to ${newFiltered.length} projects based on DU:${selectedDU}, Account:${selectedAccount}, PU:${selectedPU}`);
    setFilteredProjects(newFiltered);
  }, [selectedDU, selectedAccount, selectedPU, compassData]);

  // Define fetchQuarterlyData function - will only execute once per unique filter combination
  const fetchQuarterlyData = useCallback(async () => {
    // Generate a unique ID for this request based on current filters
    const requestId = getQuarterlyRequestId();

    console.log(`[FETCH QUARTERLY] Starting fetch with years: ${selectedYear}`);
    console.log(`REQUEST ID: ${requestId} vs CURRENT: ${requestIdRef.current.quarterly}`);

    // If this exact request has been made before, skip it
    if (requestId === requestIdRef.current.quarterly) {
      console.log('!!!SKIPPING FETCH!!! Quarterly data already fetched with these filters');
      return;
    }

    // Set loading state
    setQuarterlyChartLoading(true);

    try {
      // Save this request ID to prevent duplicates
      requestIdRef.current.quarterly = requestId;
      console.log('Executing quarterly API call for requestId:', requestId);

      // Use ALL projects when "All" is selected, otherwise use filteredProjects
      let projectIdsToUse;
      if (selectedDU.includes("All")) {
        // Get ALL project IDs from compassData when "All" is selected
        projectIdsToUse = compassData.map(p => p.projectid);
        console.log(`USING ALL ${projectIdsToUse.length} PROJECTS FOR "ALL" SELECTION`);
      } else {
        // Use filtered projects for specific DU selections
        projectIdsToUse = filteredProjects;
        console.log(`USING ${projectIdsToUse.length} FILTERED PROJECTS FOR "${selectedDU}" SELECTION`);
      }

      const projectArray = projectIdsToUse
        .map((projectId: string) => ({
          projectid: projectId,
          set_name: projectIdToSetNameMap[projectId],
        }))
        .filter((p: any) => p.projectid && p.set_name);

      console.log(`Final project array length: ${projectArray.length}`);

      if (projectArray.length === 0) {
        console.log('No projects available for quarterly data fetch');

        // Check if we have locally stored data as fallback
        try {
          const savedAssessmentData = localStorage.getItem('pbt_quarterly_assessment_status');
          const savedObservationData = localStorage.getItem('pbt_quarterly_observation_status');

          if (savedAssessmentData && savedObservationData) {
            console.log('Using locally stored data as fallback since no projects are available');
            setQuarterlyAssessmentStatus(JSON.parse(savedAssessmentData));
            setQuarterlyObservationStatus(JSON.parse(savedObservationData));
          } else {
            setQuarterlyAssessmentStatus({});
            setQuarterlyObservationStatus({});
          }
        } catch (err) {
          console.error('Error reading locally stored fallback data:', err);
          setQuarterlyAssessmentStatus({});
          setQuarterlyObservationStatus({});
        }
        return;
      }

      // Using APIClient to fetch data with caching and request deduplication
      const endpoint = `${process.env.REACT_APP_LEADERSHIP_SERVICE_BASE_URL}/gettotal-by-quarter`;
      
      // Check if we need to make separate API calls for each year
      // Some APIs don't properly handle array of years
      const yearsToFetch = Array.isArray(selectedYear) ? selectedYear : [selectedYear];
      
      console.log(`[QUARTERLY API CALL] Years to fetch:`, yearsToFetch);
      console.log(`[QUARTERLY API CALL] Project count:`, projectArray.length);
      
      const assessmentStatus: Record<string, any> = {};
      const observationStatus: Record<string, any> = {};
      
      // Fetch data for each year separately and merge results
      // Use Promise.all to fetch all years in parallel for better performance
      const yearDataPromises = yearsToFetch.map(async (year) => {
        console.log(`[QUARTERLY API CALL] Fetching data for year: ${year}`);
        
        const options = {
          method: 'POST',
          body: { year: year, projectArray },
          config: {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${sessionStorage.getItem('PBT_ID_TOKEN')}`,
            }
          }
        };
        
        console.log(`[QUARTERLY API CALL] Request body for year ${year}:`, JSON.stringify({ year: year, projectArray: projectArray.length }));

        // Use APIClient with caching enabled - it will automatically handle request deduplication
        // Cache is good because same year + same projects should return same data
        const data = await APIClient.fetch(endpoint, options);
        
        console.log(`[QUARTERLY API RESPONSE] Received ${data.length} items for year ${year}`);
        
        // Validate that we received data for the requested year
        const receivedYears = new Set(data.map((item: any) => item.year));
        
        if (!receivedYears.has(year)) {
          console.warn(`[QUARTERLY API RESPONSE] WARNING: No data received for year ${year}`);
        }
        
        return { year, data };
      });
      
      // Wait for all year data to be fetched in parallel
      const allYearData = await Promise.all(yearDataPromises);
      
      // Process all the data
      allYearData.forEach(({ year, data }) => {
        console.log(`[QUARTERLY DATA] Processing ${data.length} items for year ${year}`);
        
        // data is array: [{ year, quarter, health, observations }, ...]
        data.forEach((item: any) => {
          // Create year-qualified key to prevent data from different years overwriting each other
          const quarterKey = `${item.quarter}-${item.year}`;
        
          // Process the health data with normalized status counts, including N/A
          const healthData = item.health || {};
          if (healthData) {
            // Sum all possible N/A keys (case-insensitive)
            const naValue = (healthData["N/A"] || 0) + (healthData["n/a"] || 0) + (healthData["NA"] || 0) + (healthData["na"] || 0);
            const normalizedHealth = {
              ...healthData,
              Red: (healthData.Red || 0) + (healthData.red || 0),
              Amber: (healthData.Amber || 0) + (healthData.amber || 0),
              Green: (healthData.Green || 0) + (healthData.green || 0),
              "N/A": naValue,
            };
            // Remove lowercase and alternate N/A keys if they exist, but keep 'N/A'
            delete normalizedHealth.red;
            delete normalizedHealth.amber;
            delete normalizedHealth.green;
            delete normalizedHealth["n/a"];
            delete normalizedHealth["NA"];
            delete normalizedHealth["na"];
            assessmentStatus[quarterKey] = normalizedHealth;
          } else {
            assessmentStatus[quarterKey] = {};
          }

          // Process observation data
          const obsData = item.observations || {};
          if (obsData) {
            // Normalize observation status keys
            const normalizedObs = {
              ...obsData,
              Open: (obsData.Open || 0) + (obsData.open || 0),
              Close: (obsData.Close || 0) + (obsData.close || 0) + (obsData.Closed || 0) + (obsData.closed || 0),
              "In Progress": (obsData["In Progress"] || 0) + (obsData["in progress"] || 0),
            };

            // Remove lowercase keys if they exist
            delete normalizedObs.open;
            delete normalizedObs.close;
            delete normalizedObs.Closed;
            delete normalizedObs.closed;
            delete normalizedObs["in progress"];

            observationStatus[quarterKey] = normalizedObs;
          } else {
            observationStatus[quarterKey] = {};
          }
        });
      });
      
      console.log(`[QUARTERLY DATA] Final assessmentStatus keys:`, Object.keys(assessmentStatus));
      console.log(`[QUARTERLY DATA] Final observationStatus keys:`, Object.keys(observationStatus));
      console.log(`[QUARTERLY DATA] Assessment data:`, assessmentStatus);
      console.log(`[QUARTERLY DATA] Observation data:`, observationStatus);
      
      setQuarterlyAssessmentStatus(assessmentStatus);
      setQuarterlyObservationStatus(observationStatus);
      
      // Set loading to false after data is set
      setQuarterlyChartLoading(false);
      console.log(`[FETCH QUARTERLY] Completed successfully`);
    } catch (err) {
      console.error("Error fetching quarterly data:", err);
      setQuarterlyChartLoading(false);

      // Try to use locally stored data as fallback when API fails
      try {
        const savedAssessmentData = localStorage.getItem('pbt_quarterly_assessment_status');
        const savedObservationData = localStorage.getItem('pbt_quarterly_observation_status');

        if (savedAssessmentData && savedObservationData) {
          console.log('API call failed - Using locally stored data as fallback');
          setQuarterlyAssessmentStatus(JSON.parse(savedAssessmentData));
          setQuarterlyObservationStatus(JSON.parse(savedObservationData));
        } else {
          // No saved data available, reset to empty
          setQuarterlyAssessmentStatus({});
          setQuarterlyObservationStatus({});
        }
      } catch (localStorageErr) {
        console.error('Error reading locally stored fallback data:', localStorageErr);
        setQuarterlyAssessmentStatus({});
        setQuarterlyObservationStatus({});
      }
    }
  }, [selectedYear, filteredProjects, projectIdToSetNameMap, setQuarterlyAssessmentStatus, setQuarterlyObservationStatus, getQuarterlyRequestId, compassData, selectedDU, selectedAccount, selectedPU, setQuarterlyChartLoading]);

  // Consolidated function to fetch both health and observations data in one place
  const fetchHealthAndObservationsData = useCallback(async (requestId: string) => {
    try {
      console.log(`Health & Observations Data - REQUEST ID: ${requestId} vs CURRENT: ${requestIdRef.current.healthObservations}`);

      // Check if this is a duplicate/stale request
      if (requestId === requestIdRef.current.healthObservations) {
        console.log(`Skipping duplicate health & observations data request ${requestId}`);
        return { observationsData: [], healthData: [] };
      }

      // Update request ID and proceed with fetch
      requestIdRef.current.healthObservations = requestId;

      // Prepare health payload
      const projectArrayForHealth = filteredProjects.map((projectId: string) => ({
        projectid: projectId,
        set_name: projectIdToSetNameMap[projectId],
      })).filter((p: any) => p.projectid && p.set_name);

      // Use APIClient to fetch both endpoints in parallel with caching
      const [observationsData, healthData] = await APIClient.batchFetch([
        {
          endpoint: `${process.env.REACT_APP_OBSERVATION_SERVICE_BASE_URL}/observations`,
          options: { method: 'GET' }
        
        },
        {
          endpoint: `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/projects/health`,
          options: {
            method: 'POST',
            body: projectArrayForHealth,
            config: { headers: { "Content-Type": "application/json" } }
          }
        }
      ]);

      return { observationsData, healthData };
    } catch (error) {
      console.error("Error fetching health and observations data:", error);
      return { observationsData: [], healthData: [] };
    }
  }, [filteredProjects, projectIdToSetNameMap]);

  // Function to fetch assessment report data from the new API
  const fetchAssessmentReportData = useCallback(async () => {
    try {
      console.log('Fetching assessment report data from leadership service');
      
      // Prepare the request body with projects array
      const projectArray = filteredProjects.map((projectId: string) => ({
        projectid: projectId,
        set_name: projectIdToSetNameMap[projectId],
      })).filter((p: any) => p.projectid && p.set_name);

      console.log(`Assessment report API - using ${projectArray.length} projects`);
      
      if (projectArray.length === 0) {
        console.log('No projects available for assessment report data fetch');
        setAssessmentReportData({});
        return;
      }
      
      const endpoint = `${process.env.REACT_APP_LEADERSHIP_SERVICE_BASE_URL}/observation-status-summary-by-month`;
      const options = {
        method: 'POST',
        body: { projects: projectArray },
        config: {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('PBT_ID_TOKEN')}`,
          }
        }
      };

      const data = await APIClient.fetch(endpoint, options);
      console.log('Assessment report data received:', data);

      // Transform the data according to the mapping:
      // N/A -> Completed, Open + In Progress -> Submitted, Close -> Draft
      const transformedData: Record<string, any> = {};
      
      Object.keys(data).forEach(monthKey => {
        const monthData = data[monthKey];
        if (monthData && typeof monthData === 'object') {
          const completed = monthData["N/A"] || 0;  // N/A means Completed
          const submitted = (monthData["Open"] || 0) + (monthData["In Progress"] || 0);  // Open + In Progress means Submitted
          const draft = monthData["Close"] || 0;  // Close means Draft
          const total = monthData["total"] || completed + submitted + draft;

          transformedData[monthKey] = {
            Completed: completed,
            Submitted: submitted,
            Draft: draft,
            total: total
          };
        }
      });

      console.log('Transformed assessment report data:', transformedData);
      setAssessmentReportData(transformedData);
    } catch (error) {
      console.error('Error fetching assessment report data:', error);
      setAssessmentReportData({});
    }
  }, [filteredProjects, projectIdToSetNameMap]);

  // Combined function to fetch all monthly data in parallel
  const fetchMonthlyData = useCallback(async (projectArray: any[]) => {
    // Generate a unique ID for this request based on current filters
    const requestId = getMonthlyRequestId();

    console.log(`Monthly Data - REQUEST ID: ${requestId} vs CURRENT: ${requestIdRef.current.monthly}`);

    // If this exact request has been made before, skip it
    if (requestId === requestIdRef.current.monthly) {
      console.log('!!!SKIPPING FETCH!!! Monthly data already fetched with these filters');
      return;
    }

    try {
      // Save this request ID to prevent duplicates
      requestIdRef.current.monthly = requestId;
      console.log('Executing monthly data API calls in parallel with', projectArray.length, 'projects');

      // Log first few projects to verify what we're sending
      if (projectArray.length > 0) {
        console.log('Sample projects:', projectArray.slice(0, 3));
      }

      // Setup API requests to be executed in parallel
      const projectEndpoint = `${process.env.REACT_APP_LEADERSHIP_SERVICE_BASE_URL}/project-status-by-month`;
      const obsEndpoint = `${process.env.REACT_APP_LEADERSHIP_SERVICE_BASE_URL}/observation-status-by-month`;

      // Use APIClient to batch both requests and execute them in parallel
      console.log('Sending API requests:');
      console.log('1. POST to:', projectEndpoint);
      console.log('2. GET to:', obsEndpoint);

      const [monthlyStatusData, observationStatusData] = await APIClient.batchFetch([
        {
          endpoint: projectEndpoint,
          options: {
            method: 'POST',
            body: projectArray,
            config: { headers: { "Content-Type": "application/json" } }
          }
        },
        {
          endpoint: obsEndpoint,
          options: {
            method: 'GET',
            config: { headers: { "Content-Type": "application/json" } }
          }
        }
      ]);

      // Log the responses to help debug
      console.log('API Responses received:');
      console.log('Monthly status data type:', typeof monthlyStatusData);
      console.log('Observation status data type:', typeof observationStatusData);

      // Process project status data with additional safety checks
      const normalizedMonthlyStatus: Record<string, any> = {};

      // Handle error case where monthlyStatusData has an error property
      if (monthlyStatusData && monthlyStatusData.error === "Internal Server Error") {
        console.error('Error in monthlyStatusData:', monthlyStatusData.details);
        // Create an empty structure to prevent processing errors
        setMonthlyAssessmentStatus({});
        return; // Exit early to prevent further processing with invalid data
      }

      // Ensure monthlyStatusData is an object before processing
      const monthlyData = (monthlyStatusData && typeof monthlyStatusData === 'object' && !Array.isArray(monthlyStatusData))
        ? monthlyStatusData
        : {};

      console.log('Processing monthly status data:', typeof monthlyData, Object.keys(monthlyData).length);

      Object.keys(monthlyData).forEach(month => {
        const monthData = monthlyData[month];
        if (!monthData || typeof monthData !== 'object') {
          console.log(`Skipping invalid month data for ${month}:`, monthData);
          return;
        }

        // Sum all N/A variants
        const na = (monthData["N/A"] || 0) + (monthData["n/a"] || 0) + (monthData["NA"] || 0) + (monthData["na"] || 0);
        normalizedMonthlyStatus[month] = {
          ...monthData,
          Red: (monthData.Red || 0) + (monthData.red || 0),
          Amber: (monthData.Amber || 0) + (monthData.amber || 0),
          Green: (monthData.Green || 0) + (monthData.green || 0),
          "N/A": na,
          total: monthData.total || 0
        };
        // Remove lowercase/alternate N/A and color keys if they exist
        delete normalizedMonthlyStatus[month].red;
        delete normalizedMonthlyStatus[month].amber;
        delete normalizedMonthlyStatus[month].green;
        delete normalizedMonthlyStatus[month]["n/a"];
        delete normalizedMonthlyStatus[month]["NA"];
        delete normalizedMonthlyStatus[month]["na"];
      });

      console.log('Monthly assessment data received for', Object.keys(normalizedMonthlyStatus).length, 'months');
      setMonthlyAssessmentStatus(normalizedMonthlyStatus);

      // Process observation status data with safety checks
      const normalizedObsStatus: Record<string, any> = {};

      // Ensure observationStatusData is an object before processing
      const obsData = (observationStatusData && typeof observationStatusData === 'object' && !Array.isArray(observationStatusData))
        ? observationStatusData
        : {};

      console.log('Processing observation status data:', typeof obsData, Object.keys(obsData).length);

      Object.keys(obsData).forEach(month => {
        const monthData = obsData[month];
        if (!monthData || typeof monthData !== 'object') {
          console.log(`Skipping invalid observation data for ${month}:`, monthData);
          return;
        }

        normalizedObsStatus[month] = {
          ...monthData,
          Open: (monthData.Open || 0) + (monthData.open || 0),
          Close: (monthData.Close || 0) + (monthData.close || 0) + (monthData.Closed || 0) + (monthData.closed || 0),
          "In Progress": (monthData["In Progress"] || 0) + (monthData["in progress"] || 0),
          total: monthData.total || 0
        };
        // Remove lowercase variants if they exist
        delete normalizedObsStatus[month].open;
        delete normalizedObsStatus[month].close;
        delete normalizedObsStatus[month].Closed;
        delete normalizedObsStatus[month].closed;
        delete normalizedObsStatus[month]["in progress"];
      });

      console.log('Monthly observation data received for', Object.keys(normalizedObsStatus).length, 'months');

      // Process observation data
      // Normalize and calculate totals for each month
      const normalizedData: Record<string, any> = {};
      Object.entries(observationStatusData || {}).forEach(([key, value]: [string, any]) => {
        normalizedData[key] = {
          Open: value.Open || 0,
          "In Progress": value["In Progress"] || 0,
          Close: value.Close || 0,
          total: (value.Open || 0) + (value["In Progress"] || 0) + (value.Close || 0)
        };
      });

      // Only apply filtering if specific filters are selected (not "All")
      if (filteredProjects.length > 0 && (!selectedDU.includes("All") || !selectedAccount.includes("All") || !selectedPU.includes("All"))) {
        console.log('Applying DU/Account/PU filtering to observation data');

        // Use the consolidated function to fetch observations data
        const { observationsData } = await fetchHealthAndObservationsData(`${requestId}_obs`);

        // Initialize counters to track filtering effectiveness
        let totalFilteredObservations = 0;
        let totalObservations = 0;

        if (!Array.isArray(observationsData)) {
          console.error('Expected observationsData to be an array but got:', typeof observationsData);
          setMonthlyObservationStatus(normalizedObsStatus); // Use normalized but unfiltered data as fallback
          return;
        }

        // Count total observations for debugging
        observationsData.forEach((project: any) => {
          if (project.observations) {
            totalObservations += project.observations.length;
          }
        });

        // Create a mapping of observations by month and project ID
        const observationsByMonth: Record<string, Record<string, number>> = {};

        // First pass: Count all observations by month across all projects
        // This gives us the total count to use as the denominator for filtering ratio
        observationsData.forEach((project: any) => {
          if (!project.observations) return;

          project.observations.forEach((obs: any) => {
            const date = new Date(obs.created_date);
            const month = date.toLocaleString("default", { month: "long" });
            const year = date.getFullYear();

            const monthKey = findMonthKey(observationStatusData, month, year);
            if (!monthKey) return;

            if (!observationsByMonth[monthKey]) {
              observationsByMonth[monthKey] = { total: 0, filtered: 0 };
            }

            // Count total observations for this month
            observationsByMonth[monthKey].total += 1;

            // Count observations that match our filter criteria
            if (filteredProjects.includes(project.projectid)) {
              observationsByMonth[monthKey].filtered += 1;
              totalFilteredObservations += 1;
            }
          });
        });

        console.log(`Found ${totalObservations} total observations across all projects`);
        console.log(`Filtered to ${totalFilteredObservations} observations matching current filters`);
        console.log('Observation counts by month:', observationsByMonth);

        // Second pass: Apply filtering based on the ratio of filtered to total observations per month
        const filteredObsData: Record<string, any> = {};

        Object.keys(normalizedObsStatus).forEach((monthKey) => {
          // Initialize with zeros in case we don't have data for this month
          filteredObsData[monthKey] = {
            Open: 0,
            "In Progress": 0,
            Close: 0,
            total: 0
          };

          // If we have observation counts for this month, calculate the proper ratio
          if (observationsByMonth[monthKey]) {
            const totalForMonth = observationsByMonth[monthKey].total;
            const filteredForMonth = observationsByMonth[monthKey].filtered;

            // Only apply ratio if we have observations
            if (totalForMonth > 0) {
              const ratio = filteredForMonth / totalForMonth;
              console.log(`Month ${monthKey}: Ratio = ${filteredForMonth}/${totalForMonth} = ${ratio.toFixed(2)}`);

              // Apply the ratio to each status count
              Object.keys(normalizedObsStatus[monthKey]).forEach((status) => {
                // Skip the 'total' field which we'll calculate separately
                if (status !== 'total') {
                  filteredObsData[monthKey][status] = Math.round(
                    normalizedObsStatus[monthKey][status] * ratio
                  );
                }
              });

              // Calculate the new total based on filtered status counts
              filteredObsData[monthKey].total =
                filteredObsData[monthKey].Open +
                filteredObsData[monthKey]["In Progress"] +
                filteredObsData[monthKey].Close;
            }
          }
        });

        console.log('Original observation status data:', normalizedObsStatus);
        console.log('Filtered observation status data:', filteredObsData);

        setMonthlyObservationStatus(filteredObsData);
      } else {
        // Use all data when "All" is selected for all filters
        console.log('Using complete observation data (no filtering)');
        setMonthlyObservationStatus(normalizedObsStatus);
      }
    } catch (err) {
      console.error("Error fetching monthly data:", err);
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    }
  }, [filteredProjects, selectedDU, selectedAccount, selectedPU, setMonthlyAssessmentStatus, setMonthlyObservationStatus, getMonthlyRequestId, findMonthKey, fetchHealthAndObservationsData]);

  // Reset API call flags when component unmounts/remounts
  useEffect(() => {
    return () => {
      // Reset flags on unmount
      // quarterlyDataFetched.current = false; // Removed: not defined or needed
      // Removed undefined monthlyProjectStatusFetched and monthlyObservationStatusFetched
    };
  }, []);

  useEffect(() => {
    // Skip until initial datasets loaded to prevent duplicate first render effect
    if (!initialDataLoaded) return;

    // Always clear all data states before fetching new data to avoid stale data
    setFindingsByStatus({});
    setAssessmentsByQuarter({});
    setTotalFindingsByQuarter({});
    setAssessmentStatusByQuarter({});
    setMonthlyAssessmentStatus({});
    setMonthlyObservationStatus({});

    // Set individual chart loading states
    setMonthlyChartLoading(true);
    setFindingsChartLoading(true);
    setQuarterlyChartLoading(true);

    // Only skip if we have no projects AND specific filters are applied
    // If "All" is selected for all filters, we should still fetch data
    if (filteredProjects.length === 0 &&
      (!selectedDU.includes("All") || !selectedAccount.includes("All") || !selectedPU.includes("All"))) {
      setLoading(false);
      setMonthlyChartLoading(false);
      setFindingsChartLoading(false);
      setQuarterlyChartLoading(false);
      return;
    }

    setLoading(true);

    const fetchData = async () => {
      try {
        // Determine if any filter is actively applied (other than "All")
        // activeFilterApplied no longer needed (monthly always fetched from backend)
        // Use the consolidated function to fetch health and observations data in a single operation
        // This eliminates redundant API calls and utilizes caching
        const requestId = `health_obs_${Date.now()}`;
        const { observationsData, healthData } = await fetchHealthAndObservationsData(requestId);
        // Use the returned data directly
        const allProjects = observationsData || [];
        // Ensure healthData is an array before processing
        const assessmentStatusRes = Array.isArray(healthData) ? healthData : [];
        console.log('assessmentStatusRes type:', typeof assessmentStatusRes, Array.isArray(assessmentStatusRes));

        // ...existing logic below remains unchanged...

        const findingsByStatusLocal: any = {};
        const totalFindingsByQuarterLocal: any = {};
        const assessmentsByQuarterLocal: any = {};
        const assessmentStatusByQuarterLocal: any = {};

        const statusMap: Record<string, any> = {};
        // Make sure we're working with an array before using forEach
        if (Array.isArray(assessmentStatusRes)) {
          assessmentStatusRes.forEach((item: any) => {
            statusMap[`${item.projectid}_${item.set_name}`] = item;
          });
        } else {
          console.error('assessmentStatusRes is not an array:', assessmentStatusRes);
        }

        // Get filtered projects to iterate over
        const workingProjects = allProjects.filter((project: any) =>
          filteredProjects.includes(project.projectid)
        );

        // Iterate ONLY filtered projects (workingProjects)
        for (const project of workingProjects) {
          const { projectid, set_name, observations } = project;

          observations?.forEach((obs: any) => {
            const date = new Date(obs.created_date);
            const quarter = getQuarter(obs.created_date);
            const monthName = date.toLocaleString("default", { month: "long" });

            // Apply year / quarter / month filters
            if (
              !selectedYear.includes(date.getFullYear()) ||
              (!allQuartersSelected &&
                selectedQuarters.length > 0 &&
                !selectedQuarters.includes(quarter.split("-")[1])) ||
              (!allMonthsSelected &&
                selectedMonths.length > 0 &&
                !selectedMonths.includes(monthName))
            )
              return;

            const status = normalizeStatus(obs.status);
            if (!findingsByStatusLocal[quarter]) {
              findingsByStatusLocal[quarter] = {
                Open: 0,
                Close: 0,
                "In Progress": 0,
              };
            }
            if (status && findingsByStatusLocal[quarter][status] !== undefined) {
              findingsByStatusLocal[quarter][status] += 1;
            }
            totalFindingsByQuarterLocal[quarter] =
              (totalFindingsByQuarterLocal[quarter] || 0) + 1;
          });

          const statusObj = statusMap[`${projectid}_${set_name}`];
          if (!statusObj || !statusObj.created_date) continue;

          const statusDate = new Date(statusObj.created_date);
          const statusQuarter = getQuarter(statusObj.created_date);
          const statusMonthName = statusDate.toLocaleString("default", {
            month: "long",
          });

          if (
            !selectedYear.includes(statusDate.getFullYear()) ||
            (!allQuartersSelected &&
              selectedQuarters.length > 0 &&
              !selectedQuarters.includes(statusQuarter.split("-")[1])) ||
            (!allMonthsSelected &&
              selectedMonths.length > 0 &&
              !selectedMonths.includes(statusMonthName))
          )
            continue;

          assessmentsByQuarterLocal[statusQuarter] =
            (assessmentsByQuarterLocal[statusQuarter] || 0) + 1;
          if (!assessmentStatusByQuarterLocal[statusQuarter]) {
            assessmentStatusByQuarterLocal[statusQuarter] = {
              Red: 0,
              Amber: 0,
              Green: 0,
            };
          }
          const normalizedStatus = normalizeStatus(statusObj.projstatus);
          if (normalizedStatus && ['Red', 'Amber', 'Green'].includes(normalizedStatus)) {
            assessmentStatusByQuarterLocal[statusQuarter][normalizedStatus] += 1;
          }
        }

        setFindingsByStatus(findingsByStatusLocal);
        setTotalFindingsByQuarter(totalFindingsByQuarterLocal);
        setAssessmentsByQuarter(assessmentsByQuarterLocal);
        setAssessmentStatusByQuarter(assessmentStatusByQuarterLocal);

        // MONTHLY ASSESSMENT & OBSERVATION STATUS
        // Always fetch from backend to ensure consistent aggregation logic
        const projectArrayForHealth = filteredProjects.map((projectId: string) => ({
          projectid: projectId,
          set_name: projectIdToSetNameMap[projectId],
        })).filter((p: any) => p.projectid && p.set_name);
        fetchMonthlyData(projectArrayForHealth);

        // Fetch assessment report data from the new API
        fetchAssessmentReportData();

        // Set main loading to false immediately after API calls are done
        setLoading(false);

        // Set individual chart loading states to false after a small delay
        // This creates a nice staggered loading effect
        setTimeout(() => setMonthlyChartLoading(false), 300);
        setTimeout(() => setFindingsChartLoading(false), 600);
        setTimeout(() => setQuarterlyChartLoading(false), 900);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
        setMonthlyChartLoading(false);
        setFindingsChartLoading(false);
        setQuarterlyChartLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [
    selectedYear,
    selectedQuarters,
    selectedMonths,
    filteredProjects,
    initialDataLoaded,
    allQuartersSelected,
    allMonthsSelected,
    selectedDU,
    selectedAccount,
    selectedPU,
    projectIdToSetNameMap,
    fetchMonthlyData,
    setFindingsByStatus,
    setAssessmentsByQuarter,
    setTotalFindingsByQuarter,
    setAssessmentStatusByQuarter,
    setMonthlyAssessmentStatus,
    setMonthlyObservationStatus,
    fetchAssessmentReportData
  ]);

  // Generate month labels for all selected years
  const groupedMonthLabels: string[] = [];
  const groupedMonthKeys: { month: string; quarter: string; label: string; year: number }[] = [];
  
  // Sort years in ascending order so oldest year appears on left, current year on right
  [...selectedYear].sort((a, b) => a - b).forEach((year) => {
    ["Q1", "Q2", "Q3", "Q4"].forEach((q) => {
      quarterMonthMap[q].forEach((month) => {
        const formatted = formatMonthYear(month, year);
        groupedMonthLabels.push(formatted);
        groupedMonthKeys.push({ month, quarter: q, label: formatted, year });
      });
    });
  });

  // applyCurrentFilters removed (no longer used after refactor)

  const getFilteredMonths = () => {
    let months: string[] = [];
    if (allQuartersSelected || selectedQuarters.length === 0) {
      months = monthsOrder;
    } else {
      months = monthsOrder.filter((m) =>
        selectedQuarters.some((q) => quarterMonthMap[q].includes(m))
      );
    }
    if (!allMonthsSelected && selectedMonths.length > 0) {
      months = months.filter((m) => selectedMonths.includes(m));
    }
    return months;
  };

  const getFilteredMonthKeysAndLabels = () => {
    const months = getFilteredMonths();
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const maxYear = Math.max(...selectedYear);
    const filteredKeys = groupedMonthKeys.filter(({ month, year }) => {
      if (maxYear < currentYear) return months.includes(month);
      if (year < currentYear) return months.includes(month);
      if (year === currentYear) {
        const monthIndex = monthsOrder.indexOf(month);
        return months.includes(month) && monthIndex <= currentMonth;
      }
      return false; // Future years
    });
    const filteredLabels = filteredKeys.map((k) => k.label);
    return { filteredKeys, filteredLabels };
  };

  const {
    filteredKeys: filteredMonthKeys,
    filteredLabels: filteredMonthLabels,
  } = getFilteredMonthKeysAndLabels();

  // MOVED UP: filtering helpers and derived filtered datasets BEFORE usage
  const getFilteredMonthlyStatus = (
    statusData: Record<string, any>,
    filteredProjects: string[]
  ) => {
    if (!filteredProjects || filteredProjects.length === 0) return {};
    const filtered: Record<string, any> = {};
    Object.keys(statusData).forEach((key) => {
      if (statusData[key]?.projectids) {
        const intersection = statusData[key].projectids.filter((id: string) =>
          filteredProjects.includes(id)
        );
        if (intersection.length > 0) {
          filtered[key] = statusData[key];
        }
      } else {
        filtered[key] = statusData[key];
      }
    });
    return filtered;
  };

  const getFilteredQuarterlyStatus = (
    statusData: Record<string, any>,
    filteredProjects: string[]
  ) => {
    const filtered: Record<string, any> = {};
    
    // Keys are in format "Q1-2024", extract quarter and year parts
    const filteredQuarters = Object.keys(statusData).filter(key => {
      const parts = key.split('-');
      const quarter = parts[0]; // Extract quarter part (Q1, Q2, etc.)
      const year = parts[1]; // Extract year part (as string)
      
      // Filter by selected years and quarters
      const yearMatch = selectedYear.includes(parseInt(year));
      const quarterMatch = allQuartersSelected || selectedQuarters.includes(quarter);
      if (!quarterMatch) {
        console.log(`Excluding ${key} - quarter ${quarter} not in selected quarters`);
        return false;
      }
      
      return true;
    });

    // Filter by months - if specific months are selected, only include quarters that contain those months
    let relevantQuarters = filteredQuarters;
    if (!allMonthsSelected && selectedMonths.length > 0) {
      const quartersByMonth: Record<string, string[]> = {};
      Object.entries(quarterMonthMap).forEach(([quarter, months]) => {
        months.forEach(month => {
          if (!quartersByMonth[month]) quartersByMonth[month] = [];
          quartersByMonth[month].push(quarter as "Q1" | "Q2" | "Q3" | "Q4");
        });
      });

      // Only keep quarters that contain at least one of the selected months
      const quartersWithSelectedMonths = new Set<string>();
      selectedMonths.forEach(month => {
        if (quartersByMonth[month]) {
          quartersByMonth[month].forEach(q => quartersWithSelectedMonths.add(q));
        }
      });

      relevantQuarters = filteredQuarters.filter(key => {
        const quarter = key.split('-')[0]; // Extract quarter part
        return quartersWithSelectedMonths.has(quarter);
      });
    }

    // Copy the filtered quarters to the result
    relevantQuarters.forEach((quarterKey) => {
      filtered[quarterKey] = statusData[quarterKey];
    });
    
    return filtered;
  };

  const filteredMonthlyAssessmentStatus = getFilteredMonthlyStatus(
    // Normalize all status keys for each month (case-insensitive)
    Object.fromEntries(
      Object.entries(monthlyAssessmentStatus).map(([monthKey, statusObj]) => {
        const s = statusObj as Record<string, any>;
        // Sum all variants for each status
        const red = (s.Red || 0) + (s.red || 0);
        const amber = (s.Amber || 0) + (s.amber || 0);
        const green = (s.Green || 0) + (s.green || 0);
        const na = (s["N/A"] || 0) + (s["n/a"] || 0) + (s["NA"] || 0) + (s["na"] || 0);
        // Keep total and any other keys
        return [
          monthKey,
          {
            total: s.total || 0,
            Red: red,
            Amber: amber,
            Green: green,
            "N/A": na,
          },
        ];
      })
    ),
    filteredProjects
  );
  const filteredMonthlyObservationStatus = getFilteredMonthlyStatus(
    monthlyObservationStatus,
    filteredProjects
  );
  
  console.log(`[BEFORE FILTERING] quarterlyAssessmentStatus keys:`, Object.keys(quarterlyAssessmentStatus));
  console.log(`[BEFORE FILTERING] quarterlyAssessmentStatus data:`, quarterlyAssessmentStatus);
  console.log(`[BEFORE FILTERING] quarterlyObservationStatus keys:`, Object.keys(quarterlyObservationStatus));
  
  const filteredQuarterlyAssessmentStatus = getFilteredQuarterlyStatus(
    quarterlyAssessmentStatus,
    filteredProjects
  );
  const filteredQuarterlyObservationStatus = getFilteredQuarterlyStatus(
    quarterlyObservationStatus,
    filteredProjects
  );
  
  console.log(`[AFTER FILTERING] filteredQuarterlyAssessmentStatus keys:`, Object.keys(filteredQuarterlyAssessmentStatus));
  console.log(`[AFTER FILTERING] filteredQuarterlyAssessmentStatus data:`, filteredQuarterlyAssessmentStatus);

  // Generate chart data with fresh calculations every time filters change
  const monthlyChartData = {
    labels: filteredMonthLabels,
    datasets: [
      {
        label: "Red",
        backgroundColor: statusColors.Red,
        data: filteredMonthKeys.map(({ month, year }) => {
          // Re-find the key each time to ensure we're using fresh data
          const key = findMonthKey(
            filteredMonthlyAssessmentStatus,
            month,
            year
          );
          // Always check if the key exists and the data is available
          return key && filteredMonthlyAssessmentStatus[key] ? filteredMonthlyAssessmentStatus[key]?.Red || 0 : 0;
        }),
      },
      {
        label: "Amber",
        backgroundColor: statusColors.Amber,
        data: filteredMonthKeys.map(({ month, year }) => {
          const key = findMonthKey(
            filteredMonthlyAssessmentStatus,
            month,
            year
          );
          return key && filteredMonthlyAssessmentStatus[key] ? filteredMonthlyAssessmentStatus[key]?.Amber || 0 : 0;
        }),
      },
      {
        label: "Green",
        backgroundColor: statusColors.Green,
        data: filteredMonthKeys.map(({ month, year }) => {
          const key = findMonthKey(
            filteredMonthlyAssessmentStatus,
            month,
            year
          );
          return key && filteredMonthlyAssessmentStatus[key] ? filteredMonthlyAssessmentStatus[key]?.Green || 0 : 0;
        }),
      },
      {
        label: "NoRAG",
        backgroundColor: statusColors["N/A"],
        data: filteredMonthKeys.map(({ month, year }) => {
          const key = findMonthKey(
            filteredMonthlyAssessmentStatus,
            month,
            year
          );
          return key && filteredMonthlyAssessmentStatus[key] ? (filteredMonthlyAssessmentStatus[key]?.["N/A"] || 0) : 0;
        }),
      },
    ],
  };

  const findingsChartData = {
    labels: filteredMonthLabels,
    datasets: [
      {
        label: "Open",
        backgroundColor: statusColors.Open,
        data: filteredMonthKeys.map(({ month, year }) => {
          const key = findMonthKey(
            filteredMonthlyObservationStatus,
            month,
            year
          );
          return key ? filteredMonthlyObservationStatus[key]?.Open || 0 : 0;
        }),
      },
      {
        label: "In Progress",
        backgroundColor: statusColors["In Progress"],
        data: filteredMonthKeys.map(({ month, year }) => {
          const key = findMonthKey(
            filteredMonthlyObservationStatus,
            month,
            year
          );
          return key
            ? filteredMonthlyObservationStatus[key]?.["In Progress"] || 0
            : 0;
        }),
      },
      {
        label: "Close",
        backgroundColor: statusColors.Close,
        data: filteredMonthKeys.map(({ month, year }) => {
          const key = findMonthKey(
            filteredMonthlyObservationStatus,
            month,
            year
          );
          return key ? filteredMonthlyObservationStatus[key]?.Close || 0 : 0;
        }),
      },
    ],
    // Add the total field to help with tooltips
    totals: filteredMonthKeys.reduce<Record<string, number>>((acc, { month, year }) => {
      const key = findMonthKey(
        filteredMonthlyObservationStatus,
        month,
        year
      );
      if (key) {
        const open = filteredMonthlyObservationStatus[key]?.Open || 0;
        const inProgress = filteredMonthlyObservationStatus[key]?.["In Progress"] || 0;
        const close = filteredMonthlyObservationStatus[key]?.Close || 0;
        acc[month] = open + inProgress + close;
      }
      return acc;
    }, {}),
  };

  // Assessment Report chart data using the new API
  const assessmentReportChartData = {
    labels: filteredMonthLabels,
    datasets: [
      {
        label: "Completed",
        backgroundColor: "#27AE60", // Green color for completed
        data: filteredMonthKeys.map(({ month, year }) => {
          const key = findMonthKey(
            assessmentReportData,
            month,
            year
          );
          return key ? assessmentReportData[key]?.Completed || 0 : 0;
        }),
      },
      {
        label: "Submitted",
        backgroundColor: "#F39C12", // Orange color for submitted
        data: filteredMonthKeys.map(({ month, year }) => {
          const key = findMonthKey(
            assessmentReportData,
            month,
            year
          );
          return key ? assessmentReportData[key]?.Submitted || 0 : 0;
        }),
      },
      {
        label: "Draft",
        backgroundColor: "#E74C3C", // Red color for draft
        data: filteredMonthKeys.map(({ month, year }) => {
          const key = findMonthKey(
            assessmentReportData,
            month,
            year
          );
          return key ? assessmentReportData[key]?.Draft || 0 : 0;
        }),
      },
    ],
    // Add the total field to help with tooltips
    totals: filteredMonthKeys.reduce<Record<string, number>>((acc, { month, year }) => {
      const key = findMonthKey(
        assessmentReportData,
        month,
        year
      );
      if (key) {
        const completed = assessmentReportData[key]?.Completed || 0;
        const submitted = assessmentReportData[key]?.Submitted || 0;
        const draft = assessmentReportData[key]?.Draft || 0;
        acc[month] = completed + submitted + draft;
      }
      return acc;
    }, {}),
  };

  // Track previous filter values to detect changes
  const prevFiltersRef = useRef({
    du: ["All"],
    account: ["All"],
    pu: ["All"],
    years: [] as number[],
  });

  useEffect(() => {
    // Don't run this effect on first render before initial data is loaded
    if (!initialDataLoaded) return;

    console.log(`[FILTER CHANGE EFFECT] Triggered with selectedYear:`, selectedYear);
    console.log(`[FILTER CHANGE EFFECT] Previous years:`, prevFiltersRef.current.years);

    // Check if years have actually changed
    const yearsChanged = JSON.stringify([...selectedYear].sort()) !== JSON.stringify([...prevFiltersRef.current.years].sort());
    
    console.log(`[FILTER CHANGE EFFECT] Years changed: ${yearsChanged}`);

    // Check if we're switching TO or FROM "All" in any filter
    const switchingToAll =
      (!prevFiltersRef.current.du.includes("All") && selectedDU.includes("All")) ||
      (!prevFiltersRef.current.account.includes("All") && selectedAccount.includes("All")) ||
      (!prevFiltersRef.current.pu.includes("All") && selectedPU.includes("All"));

    const switchingFromAll =
      (prevFiltersRef.current.du.includes("All") && !selectedDU.includes("All")) ||
      (prevFiltersRef.current.account.includes("All") && !selectedAccount.includes("All")) ||
      (prevFiltersRef.current.pu.includes("All") && !selectedPU.includes("All"));

    // Reset request IDs to ensure we always fetch fresh data when filters change
    requestIdRef.current = {
      quarterly: "",
      monthly: "",
      consolidated: "",
      healthObservations: ""
    };

    console.log(`[FILTER CHANGE EFFECT] Clearing quarterly data and setting loading state`);
    // Set loading state before clearing data
    setQuarterlyChartLoading(true);
    
    // Always clear data first
    setQuarterlyAssessmentStatus({});
    setQuarterlyObservationStatus({});

    console.log(`Filter change detected. Years: ${selectedYear}, DU: ${selectedDU}, Account: ${selectedAccount}, PU: ${selectedPU}`);
    console.log(`Switching TO All: ${switchingToAll}, FROM All: ${switchingFromAll}`);

    // Update previous filter values including years
    prevFiltersRef.current = {
      du: [...selectedDU],
      account: [...selectedAccount],
      pu: [...selectedPU],
      years: [...selectedYear],
    };

    // Fetch the data - this will create a new request ID based on current filters
    console.log(`[FILTER CHANGE EFFECT] Calling fetchQuarterlyData`);
    fetchQuarterlyData();

  }, [selectedYear, selectedDU, selectedAccount, selectedPU, selectedQuarters, allQuartersSelected, projectIdToSetNameMap, filteredProjects, initialDataLoaded, fetchQuarterlyData]);

  // Add auto-refresh mechanism to prevent data disappearing
  useEffect(() => {
    // Skip if not initialized yet
    if (!initialDataLoaded) return;

    console.log('Setting up periodic data refresh to prevent data vanishing');

    // Refresh every 30 minutes (less than cache expiration but frequent enough to keep data fresh)
    const refreshInterval = 30 * 60 * 1000; // 30 minutes

    // Create refresh timer - will automatically refresh data periodically
    const refreshTimer = setInterval(() => {
      console.log('Periodic data refresh triggered');

      // Only refresh if we have data (prevent unnecessary API calls on initial load)
      if (Object.keys(quarterlyAssessmentStatus).length > 0 || Object.keys(quarterlyObservationStatus).length > 0) {
        // Reset request IDs to force new API calls
        requestIdRef.current = {
          quarterly: "",
          monthly: "",
          consolidated: "",
          healthObservations: ""
        };

        // Re-fetch quarterly data
        fetchQuarterlyData();
      }
    }, refreshInterval);

    // Clean up on unmount
    return () => {
      clearInterval(refreshTimer);
      console.log('Cleared periodic data refresh timer');
    };
  }, [fetchQuarterlyData, initialDataLoaded, quarterlyAssessmentStatus, quarterlyObservationStatus]);

  // Persist quarterly data to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(quarterlyAssessmentStatus).length > 0) {
      try {
        localStorage.setItem('pbt_quarterly_assessment_status', JSON.stringify(quarterlyAssessmentStatus));
        console.log('Saved quarterly assessment data to localStorage');
      } catch (err) {
        console.error('Error saving quarterly assessment data to localStorage:', err);
      }
    }
  }, [quarterlyAssessmentStatus]);

  // Persist quarterly observation data to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(quarterlyObservationStatus).length > 0) {
      try {
        localStorage.setItem('pbt_quarterly_observation_status', JSON.stringify(quarterlyObservationStatus));
        console.log('Saved quarterly observation data to localStorage');
      } catch (err) {
        console.error('Error saving quarterly observation data to localStorage:', err);
      }
    }
  }, [quarterlyObservationStatus]);

  // const getQuarterLabels = () => {
  //   const currentDate = new Date();
  //   const currentYear = currentDate.getFullYear();
  //   const currentMonth = currentDate.getMonth();
  //   if (selectedYear > currentYear) return [];
  //   if (selectedYear < currentYear) {
  //     return ["Q1 (Jan-Mar)", "Q2 (Apr-Jun)", "Q3 (Jul-Sep)", "Q4 (Oct-Dec)"];
  //   }
  //   const quarterLabels = [
  //     "Q1 (Jan-Mar)",
  //     "Q2 (Apr-Jun)",
  //     "Q3 (Jul-Sep)",
  //     "Q4 (Oct-Dec)",
  //   ];
  //   const currentQuarter = Math.floor(currentMonth / 3);
  //   return quarterLabels.slice(0, currentQuarter + 1);
  // };
  const getFilteredQuarterLabels = (): string[] => {
    const allLabels: Record<"Q1" | "Q2" | "Q3" | "Q4", string> = {
      Q1: "Q1 (Jan-Mar)",
      Q2: "Q2 (Apr-Jun)",
      Q3: "Q3 (Jul-Sep)",
      Q4: "Q4 (Oct-Dec)"
    };

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentQuarter = Math.floor(currentDate.getMonth() / 3) + 1;

    // Generate labels for each selected year (sorted in ascending order)
    const labels: string[] = [];
    const sortedYears = [...selectedYear].sort((a, b) => a-b);
    
    sortedYears.forEach(year => {
      let quarters: ("Q1" | "Q2" | "Q3" | "Q4")[];

      // First filter by selected quarters
      if (allQuartersSelected) {
        quarters = year < currentYear
          ? ["Q1", "Q2", "Q3", "Q4"]
          : year === currentYear
            ? (["Q1", "Q2", "Q3", "Q4"].slice(0, currentQuarter) as ("Q1" | "Q2" | "Q3" | "Q4")[])
            : [];
      } else {
        quarters = selectedQuarters.filter((q): q is "Q1" | "Q2" | "Q3" | "Q4" => q in allLabels);
        // Filter out future quarters for current year
        if (year === currentYear) {
          quarters = quarters.filter(q => {
            const qNum = parseInt(q.substring(1));
            return qNum <= currentQuarter;
          });
        }
      }

      // Then filter by selected months if needed
      if (!allMonthsSelected && selectedMonths.length > 0) {
        const quartersByMonth: Record<string, string[]> = {};
        Object.entries(quarterMonthMap).forEach(([quarter, months]) => {
          months.forEach(month => {
            if (!quartersByMonth[month]) quartersByMonth[month] = [];
            quartersByMonth[month].push(quarter as "Q1" | "Q2" | "Q3" | "Q4");
          });
        });

        // Only keep quarters that contain at least one of the selected months
        const quartersWithSelectedMonths = new Set<string>();
        selectedMonths.forEach(month => {
          if (quartersByMonth[month]) {
            quartersByMonth[month].forEach(q => quartersWithSelectedMonths.add(q));
          }
        });

        quarters = quarters.filter(q => quartersWithSelectedMonths.has(q));
      }

      // Always append year to label so the selected year is visible on the X axis
      quarters.forEach(q => {
        labels.push(`${allLabels[q]} ${year}`);
      });
    });

    return labels;
  };
  // monthlyAssessmentVsFindingsChartData removed (unused)

  // Update quarterlyAssessmentVsFindingsChartData to use filtered data based on all filters
  const quarterlyLabels = getFilteredQuarterLabels();
  
  const quarterlyAssessmentVsFindingsChartData = {
    labels: quarterlyLabels,
    datasets: [
      {
        label: "Assessment Red",
        backgroundColor: statusColors.Red,
        data: quarterlyLabels.map((label) => {
          // Parse quarter and year from label (e.g., "Q1 (Jan-Mar) 2024" or "Q1 (Jan-Mar)")
          const parts = label.split(" ");
          const quarter = parts[0];
          const year = parts.length > 2 ? parts[parts.length - 1] : selectedYear[0].toString();
          const quarterKey = `${quarter}-${year}`;
          const value = filteredQuarterlyAssessmentStatus[quarterKey]?.Red || 0;
          console.log(`Label: ${label}, Key: ${quarterKey}, Red: ${value}`);
          return value;
        }),
      },
      {
        label: "Assessment Amber",
        backgroundColor: statusColors.Amber,
        data: quarterlyLabels.map((label) => {
          const parts = label.split(" ");
          const quarter = parts[0];
          const year = parts.length > 2 ? parts[parts.length - 1] : selectedYear[0].toString();
          const quarterKey = `${quarter}-${year}`;
          return filteredQuarterlyAssessmentStatus[quarterKey]?.Amber || 0;
        }),
      },
      {
        label: "Assessment Green",
        backgroundColor: statusColors.Green,
        data: quarterlyLabels.map((label) => {
          const parts = label.split(" ");
          const quarter = parts[0];
          const year = parts.length > 2 ? parts[parts.length - 1] : selectedYear[0].toString();
          const quarterKey = `${quarter}-${year}`;
          return filteredQuarterlyAssessmentStatus[quarterKey]?.Green || 0;
        }),
      },
      {
        label: "NoRAG",
        backgroundColor: statusColors["N/A"],
        data: quarterlyLabels.map((label) => {
          const parts = label.split(" ");
          const quarter = parts[0];
          const year = parts.length > 2 ? parts[parts.length - 1] : selectedYear[0].toString();
          const quarterKey = `${quarter}-${year}`;
          return filteredQuarterlyAssessmentStatus[quarterKey]?.["N/A"] || 0;
        }),
      },
      {
        label: "Findings Open",
        backgroundColor: statusColors.Open,
        data: quarterlyLabels.map((label) => {
          const parts = label.split(" ");
          const quarter = parts[0];
          const year = parts.length > 2 ? parts[parts.length - 1] : selectedYear[0].toString();
          const quarterKey = `${quarter}-${year}`;
          return filteredQuarterlyObservationStatus[quarterKey]?.Open || 0;
        }),
      },
      {
        label: "Findings In Progress",
        backgroundColor: statusColors["In Progress"],
        data: quarterlyLabels.map((label) => {
          const parts = label.split(" ");
          const quarter = parts[0];
          const year = parts.length > 2 ? parts[parts.length - 1] : selectedYear[0].toString();
          const quarterKey = `${quarter}-${year}`;
          return (
            filteredQuarterlyObservationStatus[quarterKey]?.["In Progress"] || 0
          );
        }),
      },
      {
        label: "Findings Close",
        backgroundColor: statusColors.Close,
        data: quarterlyLabels.map((label) => {
          const parts = label.split(" ");
          const quarter = parts[0];
          const year = parts.length > 2 ? parts[parts.length - 1] : selectedYear[0].toString();
          const quarterKey = `${quarter}-${year}`;
          return filteredQuarterlyObservationStatus[quarterKey]?.Close || 0;
        }),
      },
    ],
  };

  const getMonthlyTotals = (
    data: Record<string, any>,
    keys: { month: string; quarter: string; label: string; year: number }[]
  ) => {
    const totals: Record<string, number> = {};
    keys.forEach(({ month, label, year }) => {
      const key = findMonthKey(data, month, year);
      totals[label] = key && data[key]?.total ? data[key].total : 0;
    });
    return totals;
  };

  const chartOptions = (
    title: string,
    totals: Record<string, number> = {},
    stacked = false
  ) => {
    const calculateYAxisMax = (chartData: any) => {
      if (!chartData || !chartData.datasets) return undefined;
      let maxValue = 0;
      if (stacked) {
        const count = chartData.labels?.length || 0;
        for (let i = 0; i < count; i++) {
          let sum = 0;
          chartData.datasets.forEach((ds: any) => {
            sum += ds.data[i] || 0;
          });
          maxValue = Math.max(maxValue, sum);
        }
      } else {
        chartData.datasets.forEach((ds: any) => {
          const datasetMax = Math.max(
            ...ds.data.filter((v: any) => v !== undefined)
          );
          maxValue = Math.max(maxValue, datasetMax);
        });
      }
      return maxValue > 0 ? Math.ceil(maxValue * 1.2) : 10;
    };
    return {
      responsive: true,
      plugins: {
        legend: { position: "right" as const },
        title: { display: true, text: title, font: { size: 18 } },
        subtitle: {
          display: true,
          text: "Total count displayed on top of each month",
          font: { size: 12, style: "italic" as const },
        },
        tooltip: {
          mode: "index" as const,
          intersect: false,
          callbacks: {
            afterTitle: (context: any) => {
              const label = context[0].label;
              const monthYear = label.split('-');
              const monthAbbrIndex = monthAbbr.indexOf(monthYear[0]);
              const month = monthsOrder[monthAbbrIndex];
              const year = parseInt('20' + monthYear[1]);
              const key = findMonthKey(filteredMonthlyObservationStatus, month, year);
              const total = key ? filteredMonthlyObservationStatus[key].total ||
                (filteredMonthlyObservationStatus[key].Open || 0) +
                (filteredMonthlyObservationStatus[key]["In Progress"] || 0) +
                (filteredMonthlyObservationStatus[key].Close || 0) : 0;
              return `Total Findings: ${total}`;
            },
          },
        },
        datalabels: { display: false },
        afterDraw: (chart: any) => {
          const ctx = chart.ctx;
          const xAxis = chart.scales.x;
          const yAxis = chart.scales.y;
          ctx.font = "bold 12px Arial";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillStyle = "#333";
          xAxis.ticks.forEach((tick: any, index: number) => {
            const label = chart.data.labels[index];
            const total = totals[label] || 0;
            if (total > 0) {
              const datasets = chart.data.datasets;
              let maxHeight = 0;
              if (stacked) {
                let sum = 0;
                datasets.forEach((ds: any) => {
                  sum += ds.data[index] || 0;
                });
                maxHeight = sum;
              } else {
                datasets.forEach((ds: any) => {
                  const v = ds.data[index] || 0;
                  maxHeight = Math.max(maxHeight, v);
                });
              }
              const xPos = xAxis.getPixelForTick(index);
              const yPos = yAxis.getPixelForValue(maxHeight) - 5;
              const textWidth = ctx.measureText(`Total: ${total}`).width;
              ctx.fillStyle = "rgba(255,255,255,0.7)";
              ctx.fillRect(
                xPos - textWidth / 2 - 2,
                yPos - 14,
                textWidth + 4,
                16
              );
              ctx.fillStyle = "#333";
              ctx.fillText(`Total: ${total}`, xPos, yPos);
            }
          });
        },
      },
      scales: {
        x: { stacked, title: { display: true, text: "Month-Year" } },
        y: {
          stacked,
          beginAtZero: true,
          title: { display: true, text: "Count" },
          ticks: {
            callback: (v: any) => (Number.isInteger(v) ? v : ""),
            stepSize: 1,
          },
          suggestedMax: undefined,
        },
      },
      beforeRender: (chart: any) => {
        const yAxis = chart.scales.y;
        yAxis.options.suggestedMax = calculateYAxisMax(chart.data);
      },
    };
  };

  const getQuarterTotals = () => {
    const totals: Record<string, number> = {};
    getFilteredQuarterLabels().forEach((label) => {
      // Parse quarter and year from label (e.g., "Q1 (Jan-Mar) 2024" or "Q1 (Jan-Mar)")
      const parts = label.split(" ");
      const quarter = parts[0];
      const year = parts.length > 2 ? parts[parts.length - 1] : selectedYear[0].toString();
      const quarterKey = `${quarter}-${year}`;
      
      // Calculate total from all status counts in filtered data
      const assessmentData = filteredQuarterlyAssessmentStatus[quarterKey];
      if (assessmentData) {
        // Sum assessment RAG values
        const assessmentTotal = (assessmentData.Red || 0) +
                                (assessmentData.Amber || 0) +
                                (assessmentData.Green || 0) +
                                (assessmentData["N/A"] || 0);

        // Also include findings/observation counts for the same quarter (if any)
        const observationData = filteredQuarterlyObservationStatus[quarterKey] || {};
        const findingsTotal = (observationData.Open || 0) +
                              (observationData["In Progress"] || 0) +
                              (observationData.Close || 0) +
                              (observationData.Closed || 0); // include possible 'Closed' variant

        totals[label] = assessmentTotal + findingsTotal;
      } else {
        totals[label] = 0;
      }
    });
    return totals;
  };

  // groupStackedChartOptions removed (unused)

  // Used in chart options below - eslint-disable-next-line @typescript-eslint/no-unused-vars
  const groupedUnstackedChartOptions = (
    title: string,
    totals: Record<string, number> = {}
  ) => {
    const calculateYAxisMax = (chartData: any) => {
      if (!chartData || !chartData.datasets) return undefined;
      let maxValue = 0;
      const stacks: Record<string, number[]> = {};
      chartData.datasets.forEach((ds: any) => {
        if (ds.stack) {
          if (!stacks[ds.stack]) {
            stacks[ds.stack] = Array(chartData.labels.length).fill(0);
          }
          ds.data.forEach((v: number, i: number) => {
            stacks[ds.stack][i] += v || 0;
          });
        } else {
          const datasetMax = Math.max(
            ...ds.data.filter((v: any) => v !== undefined)
          );
          maxValue = Math.max(maxValue, datasetMax);
        }
      });
      Object.values(stacks).forEach((values) => {
        const stackMax = Math.max(...values);
        maxValue = Math.max(maxValue, stackMax);
      });
      return maxValue > 0 ? Math.ceil(maxValue * 1.2) : 10;
    };
    return {
      responsive: true,
      plugins: {
        legend: { position: "right" as const },
        title: { display: true, text: title, font: { size: 18 } },
        subtitle: {
          display: true,
          text: "Total count displayed on top of each month",
          font: { size: 12, style: "italic" },
        },
        tooltip: {
          mode: "index" as const,
          intersect: false,
          callbacks: {
            afterTitle: (context: any) => {
              const label = context[0].label;
              return `Total Projects: ${label}`;
            },
          },
        },
      },
      scales: {
        x: { stacked: false, title: { display: true, text: "Month-Year" } },
        y: {
          stacked: false,
          beginAtZero: true,
          title: { display: true, text: "Count" },
          ticks: {
            callback: (v: any) => (Number.isInteger(v) ? v : ""),
            stepSize: 1,
          },
          suggestedMax: undefined,
        },
      },
      beforeRender: (chart: any) => {
        const yAxis = chart.scales.y;
        yAxis.options.suggestedMax = calculateYAxisMax(chart.data);
      },
    };
  };

  const handleQuarterChange = (e: any) => {
    const values = e.target.value as string[];
    const allQuarterOptions = ["Q1", "Q2", "Q3", "Q4"];
    
    if (values.includes("All") && !allQuartersSelected) {
      // "All" was just clicked - check all items
      setAllQuartersSelected(true);
      setSelectedQuarters(allQuarterOptions);
      setAllMonthsSelected(true);
      setSelectedMonths([]);
    } else if (!values.includes("All") && allQuartersSelected) {
      // "All" was unchecked directly - uncheck all items
      setAllQuartersSelected(false);
      setSelectedQuarters([]);
    } else if (allQuartersSelected && values.includes("All")) {
      // A specific item was clicked while "All" was selected
      const newSpecificItems = values.filter((v) => v !== "All");
      
      // When allQuartersSelected is true, clicking a specific item means unchecking it
      // The clicked item will be in newSpecificItems
      if (newSpecificItems.length > 0) {
        // The item in newSpecificItems is the one that was clicked
        const clickedItem = newSpecificItems[0];
        const remainingValues = allQuarterOptions.filter(v => v !== clickedItem);
        setAllQuartersSelected(false);
        setSelectedQuarters(remainingValues);
      } else {
        // Shouldn't happen but handle it
        setAllQuartersSelected(false);
        setSelectedQuarters([]);
      }
    } else {
      // Normal selection/deselection without "All" being involved
      const effectiveValues = values.filter(v => v !== "All");
      
      // Check if all items are now selected
      if (effectiveValues.length === allQuarterOptions.length) {
        setAllQuartersSelected(true);
        setSelectedQuarters(allQuarterOptions);
      } else {
        setAllQuartersSelected(false);
        setSelectedQuarters(effectiveValues);
      }
    }
  };

  const handleMonthChange = (e: any) => {
    const values = e.target.value as string[];
    const allMonthOptions = selectedQuarters.length === 0
      ? monthsOrder
      : monthsOrder.filter((m) =>
        selectedQuarters.some((q) =>
          quarterMonthMap[q]?.includes(m)
        )
      );
    
    if (values.includes("All") && !allMonthsSelected) {
      // "All" was just clicked - check all items
      setAllMonthsSelected(true);
      setSelectedMonths(allMonthOptions);
    } else if (!values.includes("All") && allMonthsSelected) {
      // "All" was unchecked directly - uncheck all items
      setAllMonthsSelected(false);
      setSelectedMonths([]);
    } else if (allMonthsSelected && values.includes("All")) {
      // A specific item was clicked while "All" was selected
      const newSpecificItems = values.filter((v) => v !== "All");
      
      // When allMonthsSelected is true, clicking a specific item means unchecking it
      // The clicked item will be in newSpecificItems
      if (newSpecificItems.length > 0) {
        // The item in newSpecificItems is the one that was clicked
        const clickedItem = newSpecificItems[0];
        const remainingValues = allMonthOptions.filter(v => v !== clickedItem);
        setAllMonthsSelected(false);
        setSelectedMonths(remainingValues);
      } else {
        // Shouldn't happen but handle it
        setAllMonthsSelected(false);
        setSelectedMonths([]);
      }
    } else {
      // Normal selection/deselection without "All" being involved
      const effectiveValues = values.filter(v => v !== "All");
      
      // Check if all items are now selected
      if (effectiveValues.length === allMonthOptions.length) {
        setAllMonthsSelected(true);
        setSelectedMonths(allMonthOptions);
      } else {
        setAllMonthsSelected(false);
        setSelectedMonths(effectiveValues);
      }
    }
  };

  const handleDUChange = (e: any) => {
    const newValues: string[] = e.target.value;
    requestIdRef.current = {
      quarterly: "",
      monthly: "",
      consolidated: "",
      healthObservations: ""
    };
    
    // Get all available DU options (excluding "All")
    const allSpecificOptions = Array.from(new Set(compassData.map(p => p.duname))).sort();
    const allDuOptions = ["All", ...allSpecificOptions];
    
    // Check if currently "All" represents all items (either explicit array or just ["All"])
    const isCurrentlyAllSelected = selectedDU.includes("All");
    
    if (newValues.includes("All") && !selectedDU.includes("All")) {
      // "All" was just clicked - check all items
      setSelectedDU(allDuOptions);
      setFilteredProjects(compassData.map(p => p.projectid));
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else if (!newValues.includes("All") && selectedDU.includes("All")) {
      // "All" was unchecked directly - uncheck all items
      setSelectedDU([]);
      setFilteredProjects([]);
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else if (isCurrentlyAllSelected && newValues.includes("All")) {
      // A specific item was clicked while "All" was selected
      // When selectedDU is ["All"], clicking a specific item ADDS it to newValues
      // (because MUI sees the item is not in value array)
      // So newValues becomes ["All", "clickedItem"]
      
      const newSpecificItems = newValues.filter(v => v !== "All");
      
      // If selectedDU was ["All"] and now we have ["All", "someItem"], 
      // that means "someItem" was clicked (to uncheck it visually)
      if (selectedDU.length === 1 && selectedDU[0] === "All" && newSpecificItems.length === 1) {
        // User clicked on a visually-checked item, wants to uncheck it
        const clickedItem = newSpecificItems[0];
        const remainingValues = allSpecificOptions.filter(v => v !== clickedItem);
        setSelectedDU(remainingValues);
        setFilteredProjects(compassData.filter(p => remainingValues.includes(p.duname)).map(p => p.projectid));
      } else {
        // selectedDU has explicit items, find which was unchecked
        const currentSpecificItems = selectedDU.filter(v => v !== "All");
        const uncheckedItem = currentSpecificItems.find(v => !newSpecificItems.includes(v));
        
        if (uncheckedItem) {
          const remainingValues = allSpecificOptions.filter(v => v !== uncheckedItem);
          setSelectedDU(remainingValues);
          setFilteredProjects(compassData.filter(p => remainingValues.includes(p.duname)).map(p => p.projectid));
        } else {
          // Fallback
          setSelectedDU(newValues.filter(v => v !== "All"));
          setFilteredProjects(compassData.filter(p => newValues.includes(p.duname)).map(p => p.projectid));
        }
      }
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else {
      // Normal selection/deselection without "All" being involved
      setSelectedDU(newValues);
      setFilteredProjects(compassData.filter(p => newValues.includes(p.duname)).map(p => p.projectid));
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    }
  }

  const handleAccountChange = (e: any) => {
    const newValues: string[] = e.target.value;
    requestIdRef.current = {
      quarterly: "",
      monthly: "",
      consolidated: "",
      healthObservations: ""
    };
    
    // Get all available Account options (excluding "All")
    const allSpecificOptions = Array.from(new Set(compassData.map(p => p.accountname))).sort();
    const allAccountOptions = ["All", ...allSpecificOptions];
    
    // Check if currently "All" represents all items
    const isCurrentlyAllSelected = selectedAccount.includes("All");
    
    if (newValues.includes("All") && !selectedAccount.includes("All")) {
      // "All" was just clicked - check all items
      setSelectedAccount(allAccountOptions);
      setFilteredProjects(compassData.map(p => p.projectid));
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else if (!newValues.includes("All") && selectedAccount.includes("All")) {
      // "All" was unchecked directly - uncheck all items
      setSelectedAccount([]);
      setFilteredProjects([]);
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else if (isCurrentlyAllSelected && newValues.includes("All")) {
      // A specific item was clicked while "All" was selected
      // When selectedAccount is ["All"], clicking a specific item ADDS it to newValues
      
      const newSpecificItems = newValues.filter(v => v !== "All");
      
      // If selectedAccount was ["All"] and now we have ["All", "someItem"], 
      // that means "someItem" was clicked (to uncheck it visually)
      if (selectedAccount.length === 1 && selectedAccount[0] === "All" && newSpecificItems.length === 1) {
        // User clicked on a visually-checked item, wants to uncheck it
        const clickedItem = newSpecificItems[0];
        const remainingValues = allSpecificOptions.filter(v => v !== clickedItem);
        setSelectedAccount(remainingValues);
        setFilteredProjects(compassData.filter(p => remainingValues.includes(p.accountname)).map(p => p.projectid));
      } else {
        // selectedAccount has explicit items, find which was unchecked
        const currentSpecificItems = selectedAccount.filter(v => v !== "All");
        const uncheckedItem = currentSpecificItems.find(v => !newSpecificItems.includes(v));
        
        if (uncheckedItem) {
          const remainingValues = allSpecificOptions.filter(v => v !== uncheckedItem);
          setSelectedAccount(remainingValues);
          setFilteredProjects(compassData.filter(p => remainingValues.includes(p.accountname)).map(p => p.projectid));
        } else {
          // Fallback
          setSelectedAccount(newValues.filter(v => v !== "All"));
          setFilteredProjects(compassData.filter(p => newValues.includes(p.accountname)).map(p => p.projectid));
        }
      }
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else {
      // Normal selection/deselection without "All" being involved
      setSelectedAccount(newValues);
      setFilteredProjects(compassData.filter(p => newValues.includes(p.accountname)).map(p => p.projectid));
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    }
  };

  const handlePUChange = (e: any) => {
    const newValues: string[] = e.target.value;
    requestIdRef.current = {
      quarterly: "",
      monthly: "",
      consolidated: "",
      healthObservations: ""
    };
    
    // Get all available PU options (excluding "All")
    const allSpecificOptions = Array.from(new Set(compassData.map(p => p.puname))).sort();
    const allPuOptions = ["All", ...allSpecificOptions];
    
    // Check if currently "All" represents all items
    const isCurrentlyAllSelected = selectedPU.includes("All");
    
    if (newValues.includes("All") && !selectedPU.includes("All")) {
      // "All" was just clicked - check all items
      setSelectedPU(allPuOptions);
      setFilteredProjects(compassData.map(p => p.projectid));
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else if (!newValues.includes("All") && selectedPU.includes("All")) {
      // "All" was unchecked directly - uncheck all items
      setSelectedPU([]);
      setFilteredProjects([]);
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else if (isCurrentlyAllSelected && newValues.includes("All")) {
      // A specific item was clicked while "All" was selected
      // When selectedPU is ["All"], clicking a specific item ADDS it to newValues
      
      const newSpecificItems = newValues.filter(v => v !== "All");
      
      // If selectedPU was ["All"] and now we have ["All", "someItem"], 
      // that means "someItem" was clicked (to uncheck it visually)
      if (selectedPU.length === 1 && selectedPU[0] === "All" && newSpecificItems.length === 1) {
        // User clicked on a visually-checked item, wants to uncheck it
        const clickedItem = newSpecificItems[0];
        const remainingValues = allSpecificOptions.filter(v => v !== clickedItem);
        setSelectedPU(remainingValues);
        setFilteredProjects(compassData.filter(p => remainingValues.includes(p.puname)).map(p => p.projectid));
      } else {
        // selectedPU has explicit items, find which was unchecked
        const currentSpecificItems = selectedPU.filter(v => v !== "All");
        const uncheckedItem = currentSpecificItems.find(v => !newSpecificItems.includes(v));
        
        if (uncheckedItem) {
          const remainingValues = allSpecificOptions.filter(v => v !== uncheckedItem);
          setSelectedPU(remainingValues);
          setFilteredProjects(compassData.filter(p => remainingValues.includes(p.puname)).map(p => p.projectid));
        } else {
          // Fallback
          setSelectedPU(newValues.filter(v => v !== "All"));
          setFilteredProjects(compassData.filter(p => newValues.includes(p.puname)).map(p => p.projectid));
        }
      }
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else {
      // Normal selection/deselection without "All" being involved
      setSelectedPU(newValues);
      setFilteredProjects(compassData.filter(p => newValues.includes(p.puname)).map(p => p.projectid));
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    }
  }

  const handleYearChange = (e: any) => {
    const newValues: number[] = e.target.value;
    requestIdRef.current = {
      quarterly: "",
      monthly: "",
      consolidated: "",
      healthObservations: ""
    };
    
    // Get all available years
    const allYearOptions = availableYears;
    
    // Check if "All" is being selected
    const hasAllInNew = newValues.includes("All" as any);
    const hadAllInOld = allYearsSelected;
    
    if (hasAllInNew && !hadAllInOld) {
      // "All" was just clicked - select all years
      setAllYearsSelected(true);
      setSelectedYear(allYearOptions);
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else if (!hasAllInNew && hadAllInOld) {
      // "All" was unchecked - deselect all
      setAllYearsSelected(false);
      setSelectedYear([]);
      setSelectedQuarters([]);
      setSelectedMonths([]);
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else if (hadAllInOld && hasAllInNew) {
      // A specific year was clicked while "All" was selected
      const numericValues = newValues.filter(v => typeof v === 'number') as number[];
      
      if (numericValues.length === 1) {
        // User clicked on a year to uncheck it
        const clickedYear = numericValues[0];
        const remainingYears = allYearOptions.filter(y => y !== clickedYear);
        setAllYearsSelected(false);
        setSelectedYear(remainingYears);
        
        // Filter quarters and months based on remaining years
        const currentDate = new Date();
        const maxYear = Math.max(...remainingYears);
        const currentYear = currentDate.getFullYear();
        const currentQuarter = Math.floor(currentDate.getMonth() / 3) + 1;
        const currentMonth = currentDate.getMonth();
        
        if (maxYear === currentYear) {
          setSelectedQuarters((prev) =>
            prev.filter((q) => parseInt(q.substring(1)) <= currentQuarter)
          );
          setSelectedMonths((prev) =>
            prev.filter((m) => monthsOrder.indexOf(m) <= currentMonth)
          );
        }
      } else {
        // Multiple years being modified
        setAllYearsSelected(false);
        setSelectedYear(numericValues);
      }
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    } else {
      // Normal selection/deselection
      const numericValues = newValues.filter(v => typeof v === 'number') as number[];
      setSelectedYear(numericValues);
      
      // Check if all years are selected
      if (numericValues.length === allYearOptions.length && allYearOptions.length > 0) {
        setAllYearsSelected(true);
      } else {
        setAllYearsSelected(false);
      }
      
      // Apply year filtering to quarters and months
      if (numericValues.length > 0) {
        const currentDate = new Date();
        const maxYear = Math.max(...numericValues);
        const currentYear = currentDate.getFullYear();
        const currentQuarter = Math.floor(currentDate.getMonth() / 3) + 1;
        const currentMonth = currentDate.getMonth();
        
        if (maxYear === currentYear) {
          setSelectedQuarters((prev) =>
            prev.filter((q) => parseInt(q.substring(1)) <= currentQuarter)
          );
          setSelectedMonths((prev) =>
            prev.filter((m) => monthsOrder.indexOf(m) <= currentMonth)
          );
        }
      }
      
      setQuarterlyAssessmentStatus({});
      setQuarterlyObservationStatus({});
      setMonthlyAssessmentStatus({});
      setMonthlyObservationStatus({});
    }
  };

  const monthlyChartRef = useRef(null);
  const findingsChartRef = useRef(null);
  const quarterlyAssessmentVsFindingsChartRef = useRef(null);
  const assessmentReportChartRef = useRef(null);
  // Chart.js instance refs (forwarded from react-chartjs-2 Bar components)
  const monthlyChartInstanceRef = useRef<any>(null);
  const findingsChartInstanceRef = useRef<any>(null);
  const quarterlyAssessmentVsFindingsChartInstanceRef = useRef<any>(null);
  const assessmentReportChartInstanceRef = useRef<any>(null);

  // Export All functionality - consolidates all chart screenshots and data into a single PDF
  const exportAllToPDF = async () => {
    setExportAllLoading(true);
    try {
      const pdf = new jsPDF("landscape", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;

      // Common filter info for all charts
      const commonFilterInfo = {
        Year: allYearsSelected ? "All" : selectedYear.sort((a, b) => b - a).join(", "),
        DU: selectedDU,
        Account: selectedAccount,
        PU: selectedPU,
        Quarters: allQuartersSelected ? "All" : selectedQuarters,
        Months: allMonthsSelected ? "All" : selectedMonths,
      };

      // Chart configurations
      const charts = [
        {
          title: "Monthly Assessment Status",
          ref: monthlyChartRef,
          data: monthlyChartData,
          filterInfo: commonFilterInfo,
          totals: getMonthlyTotals(filteredMonthlyAssessmentStatus, filteredMonthKeys)
        },
        {
          title: "Findings by Status per Month",
          ref: findingsChartRef,
          data: findingsChartData,
          filterInfo: commonFilterInfo,
          totals: getMonthlyTotals(filteredMonthlyObservationStatus, filteredMonthKeys)
        },
        {
          title: "Quarterly Assessment Status vs Findings Status",
          ref: quarterlyAssessmentVsFindingsChartRef,
          data: quarterlyAssessmentVsFindingsChartData,
          filterInfo: {
            Year: allYearsSelected ? "All" : selectedYear.sort((a, b) => b - a).join(", "),
            DU: selectedDU,
            Account: selectedAccount,
            PU: selectedPU,
          },
          totals: getQuarterTotals()
        },
        {
          title: "Assessment Report",
          ref: assessmentReportChartRef,
          data: assessmentReportChartData,
          filterInfo: commonFilterInfo,
          totals: getMonthlyTotals(assessmentReportData, filteredMonthKeys)
        }
      ];

      // Add cover page
      pdf.setFillColor(255, 94, 79);
      pdf.rect(0, 0, pageWidth, 30, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont("helvetica", "bold");
      pdf.text("Leadership Assessment Dashboard", pageWidth / 2, 20, { align: "center" });
      pdf.setFontSize(14);
      pdf.text(`Complete Export - Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, 28, { align: "center" });

      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text("Applied Filters", margin, 50);

      let currentY = 60;
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");

      const filterTexts = Object.entries(commonFilterInfo)
        .filter(([_, v]) => v)
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`);

      filterTexts.forEach(filterText => {
        pdf.text(filterText, margin + 5, currentY);
        currentY += 8;
      });

      // Process each chart
      for (let i = 0; i < charts.length; i++) {
        const chart = charts[i];

        if (chart.ref.current && chart.data) {
          // Add new page for each chart (except the first one)
          if (i > 0) {
            pdf.addPage();
          } else {
            pdf.addPage();
          }

          // Add header for each chart page
          pdf.setFillColor(255, 94, 79);
          pdf.rect(0, 0, pageWidth, 25, "F");
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(18);
          pdf.setFont("helvetica", "bold");
          pdf.text(chart.title, pageWidth / 2, 18, { align: "center" });

          pdf.setTextColor(0, 0, 0);
          let yPosition = 35;

          // Add chart screenshot
          try {
            const canvas = await html2canvas(chart.ref.current, {
              scale: 2,
              useCORS: true,
              backgroundColor: '#ffffff'
            });

            const imgData = canvas.toDataURL("image/png");
            const chartHeight = 100;
            pdf.addImage(imgData, "PNG", margin, yPosition, pageWidth - 2 * margin, chartHeight);
            yPosition += chartHeight + 10;
          } catch (error) {
            console.error(`Error capturing chart ${chart.title}:`, error);
            pdf.setFontSize(12);
            pdf.text(`Error capturing chart: ${chart.title}`, margin, yPosition);
            yPosition += 10;
          }

          // Add data table
          if (chart.data && chart.data.labels && chart.data.datasets) {
            pdf.setFontSize(14);
            pdf.setFont("helvetica", "bold");

            // Get custom title based on chart type
            let customTitle = "Data Table:";
            if (chart.title === "Monthly Assessment Status") {
              customTitle = "Count of Assessments by Month (Mmm-YY):";
            } else if (chart.title === "Findings by Status per Month") {
              customTitle = "Count of Findings by Month (Mmm-YY):";
            } else if (chart.title === "Quarterly Assessment Status vs Findings Status") {
              customTitle = "Count Quarterly Assessment Status vs Findings Status by Month (Mmm-YY):";
            } else if (chart.title === "Assessment Report") {
              customTitle = "Count of Assessment Report Status by Month (Mmm-YY):";
            }

            pdf.text(customTitle, margin, yPosition);
            yPosition += 8;

            // Prepare headers and data with Total column
            const visibleLabels = chart.data.labels;
            const visibleDatasets = chart.data.datasets;
            const headers = [
              "Category",
              ...visibleDatasets.map((ds: any) => ds.label === 'N/A' ? 'NoRAG' : ds.label),
              "Total" // Add Total column to headers
            ];

            const tableData = visibleLabels.map((label: any, i: number) => {
              // Get data for current row
              const rowData = visibleDatasets.map((ds: any) => ds.data[i]);
              // Calculate total by summing all numeric values in the row
              const rowTotal = rowData.reduce((sum: number, val: any) => {
                return sum + (typeof val === 'number' ? val : 0);
              }, 0);
              return [
                label,
                ...rowData,
                rowTotal, // Add total to row data
              ];
            });

            // Adjust font size and column width based on chart type
            let fontSize = 12;
            if (chart.title === "Quarterly Assessment Status vs Findings Status") {
              // Use smaller font size for the chart with many columns
              fontSize = 9; // Increased from 7 to 9 for better readability
            }

            const colWidth = (pageWidth - 2 * margin) / headers.length;

            // For Quarterly Assessment Status vs Findings Status, increase row height
            let headerRowHeight = 10;
            if (chart.title === "Quarterly Assessment Status vs Findings Status") {
              headerRowHeight = 18; // Increased from 15 to 18 for better readability with larger font
            }

            // Draw header row with styling
            pdf.setFillColor(255, 94, 79);
            pdf.rect(margin, yPosition, pageWidth - 2 * margin, headerRowHeight, "F");
            pdf.setTextColor(255, 255, 255);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(fontSize);

            // Draw each header text
            headers.forEach((h, i) => {
              // For Quarterly Assessment, add text wrapping for long headers
              if (chart.title === "Quarterly Assessment Status vs Findings Status" && h.includes(" ")) {
                const words = h.split(" ");
                const firstLine = words.slice(0, -1).join(" ");
                const secondLine = words[words.length - 1];
                pdf.text(firstLine, margin + i * colWidth + colWidth / 2, yPosition + 5, {
                  align: "center",
                });
                pdf.text(secondLine, margin + i * colWidth + colWidth / 2, yPosition + 12, {
                  align: "center",
                });
              } else {
                pdf.text(h, margin + i * colWidth + colWidth / 2, yPosition + (headerRowHeight / 2) + 1, {
                  align: "center",
                });
              }
            });

            pdf.setTextColor(0, 0, 0);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(fontSize);

            // Adjust yPosition based on header row height
            yPosition += headerRowHeight;

            // Set data row height (same as header if using Quarterly Assessment chart)
            let dataRowHeight = 10;
            if (chart.title === "Quarterly Assessment Status vs Findings Status") {
              dataRowHeight = 14; // Increased from 12 to 14 for better readability with larger font
            }
            // Table rows with styling
            tableData.forEach((row: any, rowIndex: number) => {
              if (yPosition + dataRowHeight > pageHeight - 20) {
                pdf.addPage();
                yPosition = 30;
              }
              // Alternating row colors
              if (rowIndex % 2 === 0) {
                pdf.setFillColor(240, 240, 240);
              } else {
                pdf.setFillColor(255, 255, 255);
              }
              pdf.rect(margin, yPosition, pageWidth - 2 * margin, dataRowHeight, "F");

              row.forEach((cell: any, cellIndex: number) => {
                const cellValue = typeof cell === "number" ? cell.toString() : cell;

                // Apply color formatting based on chart type and column
                if (chart.title === "Monthly Assessment Status") {
                  if (headers[cellIndex] === "Red") {
                    pdf.setTextColor(255, 0, 0); // Red
                  } else if (headers[cellIndex] === "Amber") {
                    pdf.setTextColor(255, 191, 0); // Amber
                  } else if (headers[cellIndex] === "Green") {
                    pdf.setTextColor(0, 128, 0); // Green
                  } else if (headers[cellIndex] === "NoRAG" || headers[cellIndex] === "N/A") {
                    pdf.setTextColor(105, 105, 105); // Deep Ash
                  } else {
                    pdf.setTextColor(0, 0, 0); // Black (default)
                  }
                } else if (chart.title === "Findings by Status per Month") {
                  if (headers[cellIndex] === "Open") {
                    pdf.setTextColor(0, 120, 212); // Light Blue
                  } else if (headers[cellIndex] === "In Progress") {
                    pdf.setTextColor(255, 191, 0); // Yellow
                  } else if (headers[cellIndex] === "Closed") {
                    pdf.setTextColor(118, 195, 65); // Light Green
                  } else {
                    pdf.setTextColor(0, 0, 0); // Black (default)
                  }
                } else if (chart.title === "Quarterly Assessment Status vs Findings Status") {
                  if (headers[cellIndex] === "Assessment Red") {
                    pdf.setTextColor(255, 0, 0); // Red
                  } else if (headers[cellIndex] === "Assessment Amber") {
                    pdf.setTextColor(255, 191, 0); // Amber
                  } else if (headers[cellIndex] === "Assessment Green") {
                    pdf.setTextColor(0, 128, 0); // Green
                  } else if (headers[cellIndex] === "Assessment NoRAG") {
                    pdf.setTextColor(105, 105, 105); // Deep Ash
                  } else if (headers[cellIndex] === "Findings Open") {
                    pdf.setTextColor(0, 120, 212); // Light Blue
                  } else if (headers[cellIndex] === "Findings In Progress") {
                    pdf.setTextColor(255, 191, 0); // Yellow
                  } else if (headers[cellIndex] === "Findings Closed") {
                    pdf.setTextColor(118, 195, 65); // Light Green
                  } else {
                    pdf.setTextColor(0, 0, 0); // Black (default)
                  }
                } else if (chart.title === "Assessment Report") {
                  if (headers[cellIndex] === "Completed") {
                    pdf.setTextColor(0, 128, 0); // Green
                  } else if (headers[cellIndex] === "Submitted") {
                    pdf.setTextColor(255, 191, 0); // Orange
                  } else if (headers[cellIndex] === "Draft") {
                    pdf.setTextColor(255, 0, 0); // Red
                  } else {
                    pdf.setTextColor(0, 0, 0); // Black (default)
                  }
                } else {
                  pdf.setTextColor(0, 0, 0); // Default black for other chart types
                }

                pdf.text(
                  cellValue,
                  margin + cellIndex * colWidth + colWidth / 2,
                  yPosition + (dataRowHeight / 2) + 1,
                  { align: "center" }
                );

                // Reset text color to black after drawing the text
                pdf.setTextColor(0, 0, 0);
              });

              yPosition += dataRowHeight;
            });

          }
        }
      }

      // Save the PDF
      pdf.save(`Leadership_Assessment_Dashboard_Complete_Export_${new Date().toISOString().split("T")[0]}.pdf`);
      setTimeout(() => {
        toast.success('Pdf export downloaded successfully!');
      }, 500);
    } catch (error) {
      console.error("Error exporting all charts to PDF:", error);
      alert("Export failed. Please try again.");
    } finally {
      setExportAllLoading(false);
    }
  };
  const exportAllToExcel = async () => {
    setExportAllLoading(true);
    try {
      console.log('Starting Excel export...');
      const workbook = new ExcelJS.Workbook();

      // Common filter info for all charts
      const yearDisplay = (selectedYear.length === 0 || selectedYear.length === availableYears.length) ? "All" : selectedYear.join(", ");
      const commonFilterInfo = {
        Year: allYearsSelected ? "All" : selectedYear.sort((a, b) => b - a).join(", "),
        DU: selectedDU,
        Account: selectedAccount,
        PU: selectedPU,
        Quarters: allQuartersSelected ? "All" : selectedQuarters,
        Months: allMonthsSelected ? "All" : selectedMonths,
      };

      // Chart configurations with better error handling and truncated names for Excel
      const charts = [
        {
          title: "Monthly Assessment Status",
          shortName: "Monthly_Assessment", // Add short name for worksheet
          ref: monthlyChartRef,
          data: monthlyChartData,
          filterInfo: commonFilterInfo,
          totals: getMonthlyTotals(filteredMonthlyAssessmentStatus, filteredMonthKeys)
        },
        {
          title: "Findings by Status per Month",
          shortName: "Findings_by_Month", // Add short name for worksheet
          ref: findingsChartRef,
          data: findingsChartData,
          filterInfo: commonFilterInfo,
          totals: getMonthlyTotals(filteredMonthlyObservationStatus, filteredMonthKeys)
        },
        {
          title: "Quarterly Assessment Status vs Findings Status",
          shortName: "Quarterly_Assessment_vs_Findings", // Add short name for worksheet
          ref: quarterlyAssessmentVsFindingsChartRef,
          data: quarterlyAssessmentVsFindingsChartData,
          filterInfo: commonFilterInfo,
          totals: getQuarterTotals()
        },
        {
          title: "Assessment Report",
          shortName: "Assessment_Report", // Add short name for worksheet
          ref: assessmentReportChartRef,
          data: assessmentReportChartData,
          filterInfo: commonFilterInfo,
          totals: getMonthlyTotals(assessmentReportData, filteredMonthKeys)
        }
      ];

      // Validate that we have valid chart data before proceeding
      const validCharts = charts.filter(chart => {
        if (!chart.data || !chart.data.labels || !chart.data.datasets) {
          console.warn(`Invalid data for chart: ${chart.title}`);
          return false;
        }
        return true;
      });

      if (validCharts.length === 0) {
        throw new Error("No valid chart data available for export");
      }

      console.log(`Processing ${validCharts.length} valid charts...`);

      // Add summary worksheet
      const summaryWorksheet = workbook.addWorksheet("Dashboard_Summary");

      // Add header
      summaryWorksheet.addRow(["Leadership Assessment Dashboard"]);
      summaryWorksheet.addRow([`Complete Export - Generated on: ${new Date().toLocaleDateString()}`]);
      summaryWorksheet.addRow([]);

      // Style the header
      const headerRow = summaryWorksheet.getRow(1);
      headerRow.font = { size: 16, bold: true, color: { argb: "FF5E4F" } };
      const subHeaderRow = summaryWorksheet.getRow(2);
      subHeaderRow.font = { size: 12, italic: true };

      // Add filter information
      summaryWorksheet.addRow(["Applied Filters:"]);
      const filterHeaderRow = summaryWorksheet.getRow(4);
      filterHeaderRow.font = { bold: true };

      Object.entries(commonFilterInfo)
        .filter(([_, v]) => v)
        .forEach(([k, v]) => {
          summaryWorksheet.addRow([
            k + ":",
            Array.isArray(v) ? v.join(", ") : String(v),
          ]);
        });

      summaryWorksheet.addRow([]);
      summaryWorksheet.addRow(["Charts Included:"]);
      const chartsHeaderRow = summaryWorksheet.getRow(summaryWorksheet.rowCount);
      chartsHeaderRow.font = { bold: true };

      validCharts.forEach(chart => {
        summaryWorksheet.addRow([`• ${chart.title}`]);
      });

      // Keep track of created worksheet names to avoid duplicates
      const createdWorksheetNames = new Set<string>();
      createdWorksheetNames.add("Dashboard_Summary");

      // Function to create safe worksheet name
      const createSafeWorksheetName = (baseName: string, suffix?: string): string => {
        let fullName = suffix ? `${baseName}_${suffix}` : baseName;

        // Replace invalid characters
        fullName = fullName.replace(/[\\\/\[\]:*?'"]/g, '_');

        // Ensure name is unique and within 31 character limit
        if (fullName.length > 31) {
          const maxBaseLength = suffix ? 31 - suffix.length - 1 : 31;
          fullName = suffix ? `${baseName.substring(0, maxBaseLength)}_${suffix}` : baseName.substring(0, 31);
        }

        // Make sure it's unique
        let finalName = fullName;
        let counter = 1;
        while (createdWorksheetNames.has(finalName)) {
          const counterStr = `_${counter}`;
          const maxLength = 31 - counterStr.length;
          finalName = `${fullName.substring(0, maxLength)}${counterStr}`;
          counter++;
        }

        createdWorksheetNames.add(finalName);
        return finalName;
      };

      // Process each valid chart
      for (const chart of validCharts) {
        try {
          console.log(`Processing chart: ${chart.title}`);

          // Validate chart data more thoroughly
          if (!chart.data || !Array.isArray(chart.data.labels) || !Array.isArray(chart.data.datasets)) {
            console.warn(`Skipping chart ${chart.title} due to invalid data structure`);
            continue;
          }

          // Create data worksheet with safe name
          const dataWorksheetName = createSafeWorksheetName(chart.shortName, "Data");
          const worksheet = workbook.addWorksheet(dataWorksheetName);

          // Add chart title and metadata
          worksheet.addRow([chart.title]);
          worksheet.addRow([`Generated on: ${new Date().toLocaleDateString()}`]);
          worksheet.addRow([]);

          // Style the title
          const titleRow = worksheet.getRow(1);
          titleRow.font = { size: 14, bold: true, color: { argb: "FF5E4F" } };

          // Add filter information for this chart
          worksheet.addRow(["Applied Filters:"]);
          const filterRow = worksheet.getRow(4);
          filterRow.font = { bold: true };

          Object.entries(chart.filterInfo)
            .filter(([_, v]) => v)
            .forEach(([k, v]) => {
              worksheet.addRow([
                k + ":",
                Array.isArray(v) ? v.join(", ") : String(v),
              ]);
            });

          worksheet.addRow([]);

          // Get custom title based on chart type
          let customTitle = "Data Table:";
          if (chart.title === "Monthly Assessment Status") {
            customTitle = "Count of Assessments by Month (Mmm-YY):";
          } else if (chart.title === "Findings by Status per Month") {
            customTitle = "Count of Findings by Month (Mmm-YY):";
          } else if (chart.title === "Quarterly Assessment Status vs Findings Status") {
            customTitle = "Count Quarterly Assessment Status vs Findings Status by Quarter:";
          } else if (chart.title === "Assessment Report") {
            customTitle = "Count of Assessment Report Status by Month (Mmm-YY):";
          }

          worksheet.addRow([customTitle]);
          const dataHeaderRow = worksheet.getRow(worksheet.rowCount);
          dataHeaderRow.font = { bold: true };

          // Prepare headers and data with Total column - with better validation
          const visibleLabels = chart.data.labels || [];
          const visibleDatasets = chart.data.datasets || [];

          if (visibleLabels.length === 0 || visibleDatasets.length === 0) {
            console.warn(`No data available for chart: ${chart.title}`);
            worksheet.addRow(["No data available for this chart"]);
            continue;
          }

          const headers = [
            "Category",
            ...visibleDatasets.map((ds: any) => ds.label === 'N/A' ? 'NoRAG' : (ds.label || 'Unknown')),
            "Total"
          ];

          const headerRow = worksheet.addRow(headers);

          // Style the header row
          headerRow.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FF5E4F" },
            };
            cell.font = { color: { argb: "FFFFFF" }, bold: true };
          });

          const tableData = visibleLabels.map((label: any, i: number) => {
            // Get data for current row with better error handling
            const rowData = visibleDatasets.map((ds: any) => {
              if (!ds.data || !Array.isArray(ds.data) || i >= ds.data.length) {
                return 0;
              }
              return ds.data[i] || 0;
            });

            // Calculate total by summing all numeric values in the row
            const rowTotal = rowData.reduce((sum: number, val: any) => {
              const numVal = typeof val === 'number' ? val : (parseFloat(val) || 0);
              return sum + numVal;
            }, 0);

            return [
              label || 'Unknown',
              ...rowData,
              rowTotal,
            ];
          });

          // Get column indexes for specific labels based on chart type
          const columnIndexes: Record<string, number> = {};
          visibleDatasets.forEach((ds: any, index: any) => {
            const labelKey = ds.label === 'N/A' ? 'NoRAG' : (ds.label || 'Unknown');
            if (labelKey) {
              columnIndexes[labelKey] = index + 2; // +2 because Excel is 1-indexed and first column is Category
            }
          });

          // Add data rows with color formatting
          tableData.forEach((row: any) => {
            const dataRow = worksheet.addRow(row);

            // Apply color formatting to each data cell based on chart type and column
            if (chart.title === "Monthly Assessment Status") {
              // Apply text color for Monthly Assessment Status columns
              if (columnIndexes['Red']) {
                const cell = dataRow.getCell(columnIndexes['Red']);
                cell.font = { color: { argb: "FF0000" } }; // Red
              }
              if (columnIndexes['Amber']) {
                const cell = dataRow.getCell(columnIndexes['Amber']);
                cell.font = { color: { argb: "FFBF00" } }; // Amber
              }
              if (columnIndexes['Green']) {
                const cell = dataRow.getCell(columnIndexes['Green']);
                cell.font = { color: { argb: "008000" } }; // Green
              }
              if (columnIndexes['NoRAG']) {
                const cell = dataRow.getCell(columnIndexes['NoRAG']);
                cell.font = { color: { argb: "696969" } }; // Deep Ash
              }
            } else if (chart.title === "Findings by Status per Month") {
              // Apply text color for Findings by Status per Month columns
              if (columnIndexes['Open']) {
                const cell = dataRow.getCell(columnIndexes['Open']);
                cell.font = { color: { argb: "0078D4" } }; // Light Blue
              }
              if (columnIndexes['In Progress']) {
                const cell = dataRow.getCell(columnIndexes['In Progress']);
                cell.font = { color: { argb: "FFBF00" } }; // Yellow
              }
              if (columnIndexes['Closed']) {
                const cell = dataRow.getCell(columnIndexes['Closed']);
                cell.font = { color: { argb: "76C341" } }; // Light Green
              }
            } else if (chart.title === "Quarterly Assessment Status vs Findings Status") {
              // Apply text color for Quarterly Assessment Status vs Findings Status columns
              if (columnIndexes['Assessment Red']) {
                const cell = dataRow.getCell(columnIndexes['Assessment Red']);
                cell.font = { color: { argb: "FF0000" } }; // Red
              }
              if (columnIndexes['Assessment Amber']) {
                const cell = dataRow.getCell(columnIndexes['Assessment Amber']);
                cell.font = { color: { argb: "FFBF00" } }; // Amber
              }
              if (columnIndexes['Assessment Green']) {
                const cell = dataRow.getCell(columnIndexes['Assessment Green']);
                cell.font = { color: { argb: "008000" } }; // Green
              }
              if (columnIndexes['NoRAG']) {
                const cell = dataRow.getCell(columnIndexes['NoRAG']);
                cell.font = { color: { argb: "696969" } }; // Deep Ash
              }
              if (columnIndexes['Findings Open']) {
                const cell = dataRow.getCell(columnIndexes['Findings Open']);
                cell.font = { color: { argb: "0078D4" } }; // Light Blue
              }
              if (columnIndexes['Findings In Progress']) {
                const cell = dataRow.getCell(columnIndexes['Findings In Progress']);
                cell.font = { color: { argb: "FFBF00" } }; // Yellow
              }
              if (columnIndexes['Findings Closed']) {
                const cell = dataRow.getCell(columnIndexes['Findings Closed']);
                cell.font = { color: { argb: "76C341" } }; // Light Green
              }
            } else if (chart.title === "Assessment Report") {
              // Apply text color for Assessment Report columns
              if (columnIndexes['Completed']) {
                const cell = dataRow.getCell(columnIndexes['Completed']);
                cell.font = { color: { argb: "008000" } }; // Green
              }
              if (columnIndexes['Submitted']) {
                const cell = dataRow.getCell(columnIndexes['Submitted']);
                cell.font = { color: { argb: "FFBF00" } }; // Orange
              }
              if (columnIndexes['Draft']) {
                const cell = dataRow.getCell(columnIndexes['Draft']);
                cell.font = { color: { argb: "FF0000" } }; // Red
              }
            }
          });

          // Auto-fit columns
          worksheet.columns.forEach(column => {
            let maxLength = 0;
            column.eachCell?.({ includeEmpty: true }, (cell) => {
              const columnLength = cell.value ? cell.value.toString().length : 10;
              if (columnLength > maxLength) {
                maxLength = columnLength;
              }
            });
            column.width = maxLength < 10 ? 10 : maxLength + 2;
          });

          // Capture screenshot and add to separate image worksheet
          if (chart.ref && chart.ref.current) {
            try {
              console.log(`Capturing screenshot for ${chart.title}...`);

              // Add a small delay before capturing to ensure chart is fully rendered
              await new Promise(resolve => setTimeout(resolve, 1000));

              const canvas = await html2canvas(chart.ref.current, {
                backgroundColor: '#ffffff',
                scale: 1, // Reduced scale to prevent memory issues
                useCORS: true,
                allowTaint: true,
                width: (chart.ref.current as HTMLElement).offsetWidth,
                height: (chart.ref.current as HTMLElement).offsetHeight,
                logging: false,
                ignoreElements: (element) => {
                  // Ignore any overlay elements that might interfere
                  return element.classList?.contains('MuiIconButton-root') ||
                    element.classList?.contains('MuiMenu-root') ||
                    element.classList?.contains('MuiTooltip-tooltip');
                }
              });

              const imgData = canvas.toDataURL("image/png", 0.8); // Reduced quality to prevent size issues
              console.log(`Screenshot captured successfully for ${chart.title}`);

              // Create a separate worksheet for the chart image with safe name
              const imageWorksheetName = createSafeWorksheetName(chart.shortName, "Chart");
              const imageWorksheet = workbook.addWorksheet(imageWorksheetName);

              // Add title to the image worksheet
              imageWorksheet.addRow([`${chart.title} - Chart Image`]);
              imageWorksheet.addRow([`Generated on: ${new Date().toLocaleDateString()}`]);
              imageWorksheet.addRow([]);

              // Style the title
              const imageTitleRow = imageWorksheet.getRow(1);
              imageTitleRow.font = { size: 14, bold: true, color: { argb: "FF5E4F" } };

              // Add filter information for this chart image
              imageWorksheet.addRow(["Applied Filters:"]);
              const imageFilterRow = imageWorksheet.getRow(4);
              imageFilterRow.font = { bold: true };

              Object.entries(chart.filterInfo)
                .filter(([_, v]) => v)
                .forEach(([k, v]) => {
                  imageWorksheet.addRow([
                    k + ":",
                    Array.isArray(v) ? v.join(", ") : String(v),
                  ]);
                });

              imageWorksheet.addRow([]);

              // Add the image
              const imageId = workbook.addImage({
                base64: imgData,
                extension: "png",
              });

              // Position the image below the filter information
              imageWorksheet.addImage(imageId, {
                tl: { col: 0, row: imageWorksheet.rowCount },
                ext: { width: 800, height: 400 }, // Reduced size to prevent issues
              });

              console.log(`Image added to worksheet for ${chart.title}`);

            } catch (err) {
              console.error(`Error capturing screenshot for ${chart.title}:`, err);

              // Add error message to worksheet instead of failing silently
              const errorWorksheetName = createSafeWorksheetName(chart.shortName, "Error");
              const errorWorksheet = workbook.addWorksheet(errorWorksheetName);
              errorWorksheet.addRow([`Error capturing screenshot for ${chart.title}`]);
              errorWorksheet.addRow([`Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
              errorWorksheet.addRow([`Please try exporting this chart individually.`]);

              const errorRow = errorWorksheet.getRow(1);
              errorRow.font = { size: 14, bold: true, color: { argb: "FF0000" } };
            }
          } else {
            console.warn(`Chart ref not available for ${chart.title}`);

            // Add warning message to worksheet
            const warningWorksheetName = createSafeWorksheetName(chart.shortName, "Warning");
            const warningWorksheet = workbook.addWorksheet(warningWorksheetName);
            warningWorksheet.addRow([`Chart reference not available for ${chart.title}`]);
            warningWorksheet.addRow([`Chart may not be fully loaded. Please try again.`]);

            const warningRow = warningWorksheet.getRow(1);
            warningRow.font = { size: 14, bold: true, color: { argb: "FFA500" } };
          }

        } catch (chartError) {
          console.error(`Error processing chart ${chart.title}:`, chartError);

          // Create error worksheet for this specific chart
          const errorWorksheetName = createSafeWorksheetName(chart.shortName, "ChartError");
          const errorWorksheet = workbook.addWorksheet(errorWorksheetName);
          errorWorksheet.addRow([`Failed to process chart: ${chart.title}`]);
          errorWorksheet.addRow([`Error: ${chartError instanceof Error ? chartError.message : 'Unknown error'}`]);

          const errorRow = errorWorksheet.getRow(1);
          errorRow.font = { size: 14, bold: true, color: { argb: "FF0000" } };
        }
      }

      // Generate and save the Excel file
      console.log('Generating Excel file...');
      const buffer = await workbook.xlsx.writeBuffer();
      console.log('Excel buffer generated successfully');

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const filename = `Leadership_Assessment_Dashboard_Complete_Export_${new Date().toISOString().split("T")[0]}.xlsx`;
      console.log(`Saving file: ${filename}`);

      saveAs(blob, filename);
      console.log('Excel export completed successfully');

      // Show success message
      setTimeout(() => {
        toast.success('Excel export downloaded successfully!');
      }, 500);

    } catch (error) {
      console.error("Error exporting all charts to Excel:", error);

      // More specific error messages
      if (error instanceof Error) {
        if (error.message.includes('html2canvas')) {
          alert("Excel export failed due to chart image capture. Please try again or contact support.");
        } else if (error.message.includes('writeBuffer')) {
          alert("Excel export failed during file generation. The file may be too large. Please try with fewer charts.");
        } else if (error.message.includes('No valid chart data')) {
          alert("Excel export failed: No chart data available. Please ensure charts have loaded properly.");
        } else if (error.message.includes('Worksheet name already exists')) {
          alert("Excel export failed due to duplicate worksheet names. This has been fixed - please try again.");
        } else {
          alert(`Excel export failed: ${error.message}. Please try again.`);
        }
      } else {
        alert("Excel export failed due to an unknown error. Please try again.");
      }
    } finally {
      setExportAllLoading(false);
    }
  };

  // Export menu handlers
  const handleExportMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
  };

  const handleExportToPDF = () => {
    handleExportMenuClose();
    exportAllToPDF();
  };

  const handleExportToExcel = () => {
    handleExportMenuClose();
    exportAllToExcel();
  };

  useEffect(() => {
    if (Object.keys(monthlyObservationStatus).length > 0) {
      console.log("Monthly Observation Status data:", monthlyObservationStatus);
    }
    if (Object.keys(monthlyAssessmentStatus).length > 0) {
      console.log("Monthly Assessment Status data:", monthlyAssessmentStatus);
    }
  }, [monthlyObservationStatus, monthlyAssessmentStatus, filteredMonthKeys]);

  useEffect(() => {
    if (Object.keys(projectIdToSetNameMap).length > 0) {
      console.log("Project ID to Set Name Map:", projectIdToSetNameMap);
    }
  }, [projectIdToSetNameMap, filteredProjects]);

  const TotalCountsRow = ({
    labels,
    totals,
    title = "Total Projects",
    paddingLeft = "55px",
    paddingRight = "85px",
    itemWidth = 110,
    maxItemWidth = "70px",
    fontSize = "0.60rem",
    containerRef, // DOM ref of the chart wrapper div
    chartInstanceRef, // Chart.js instance ref from Bar component
  }: {
    labels: string[];
    totals: Record<string, number>;
    title?: string;
    paddingLeft?: string;
    paddingRight?: string;
    itemWidth?: number;
    maxItemWidth?: string;
    fontSize?: string;
    containerRef?: any;
    chartInstanceRef?: any;
  }) => {
    const [positions, setPositions] = useState<number[]>([]);
    const localContainerRef = useRef<HTMLDivElement | null>(null);

    // Provide a ref to compute offsets; prefer provided containerRef
    useEffect(() => {
      const computePositions = () => {
        try {
          const chart = chartInstanceRef?.current;
          const containerEl = (containerRef && containerRef.current) || localContainerRef.current;
          if (!chart || !chart.chartArea || !containerEl) {
            setPositions([]);
            return;
          }

          const chartArea = chart.chartArea;
          const canvasRect = chart.canvas.getBoundingClientRect();
          const containerRect = containerEl.getBoundingClientRect();

          const labelCount = labels.length || 1;
          const newPositions = labels.map((_, i) => {
            // x position relative to viewport: canvas left + chartArea.left + center offset
            const x = canvasRect.left + chartArea.left + ((i + 0.5) * chartArea.width) / labelCount;
            // convert to position relative to container element
            return Math.round(x - containerRect.left);
          });

          setPositions(newPositions);
        } catch (err) {
          setPositions([]);
        }
      };

        computePositions();

        // Poll for chart readiness because Chart.js may not have finished
        // calculating chartArea/canvas immediately after mount. Retry a few
        // times with an interval so totals reliably align for all charts
        // (fixes intermittent missing totals for the Findings chart).
        let attempts = 0;
        const maxAttempts = 12; // try for up to ~2.4s (12 * 200ms)
        const interval = window.setInterval(() => {
          attempts += 1;
          try {
            const chart = chartInstanceRef?.current;
            const containerEl = (containerRef && containerRef.current) || localContainerRef.current;
            if (chart && chart.chartArea && containerEl) {
              computePositions();
              window.clearInterval(interval);
            } else if (attempts >= maxAttempts) {
              // give up after max attempts to avoid leaking interval
              window.clearInterval(interval);
            }
          } catch (e) {
            if (attempts >= maxAttempts) window.clearInterval(interval);
          }
        }, 200);

        const onResize = () => computePositions();
        window.addEventListener("resize", onResize);
        return () => {
          window.removeEventListener("resize", onResize);
          window.clearInterval(interval);
        };
    }, [labels, chartInstanceRef, containerRef, itemWidth]);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", mb: 1, mt: 0 }} ref={localContainerRef}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
          {title}:
        </Typography>

        <Box sx={{ position: 'relative', height: '28px', pb: 1, borderBottom: '1px dashed #ccc' }}>
          {positions && positions.length === labels.length ? (
            labels.map((label, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  position: 'absolute',
                  left: `${positions[index]}px`,
                  top: 0,
                  transform: 'translateX(-50%)',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: fontSize,
                  whiteSpace: 'nowrap',
                }}
              >
                {totals[label] || 0}
              </Typography>
            ))
          ) : (
            // Fallback: evenly distribute when positions not available yet
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: paddingLeft, pr: paddingRight }}>
              {labels.map((label, index) => (
                <Typography key={index} variant="body2" sx={{ fontWeight: 'bold', fontSize: fontSize }}>
                  {totals[label] || 0}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const isQuarterInFuture = (quarter: string): boolean => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentQuarter = Math.floor(currentDate.getMonth() / 3) + 1;
    const maxYear = Math.max(...selectedYear);
    if (maxYear > currentYear) return true;
    if (maxYear < currentYear) return false;
    const quarterNumber = parseInt(quarter.substring(1));
    
    // If no years selected or all years selected, check against current year
    if (selectedYear.length === 0 || selectedYear.length === availableYears.length) {
      return quarterNumber > currentQuarter;
    }
    
    // If all selected years are in the future, quarter is definitely in future
    if (selectedYear.every(y => y > currentYear)) return true;
    
    // If all selected years are in the past, quarter is not in future
    if (selectedYear.every(y => y < currentYear)) return false;
    
    // If current year is selected, check the quarter
    if (selectedYear.includes(currentYear)) {
      return quarterNumber > currentQuarter;
    }
    
    return false;
  };
  
  const isMonthInFuture = (month: string): boolean => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const maxYear = Math.max(...selectedYear);
    if (maxYear > currentYear) return true;
    if (maxYear < currentYear) return false;
    const monthIndex = monthsOrder.indexOf(month);
    
    // If no years selected or all years selected, check against current year
    if (selectedYear.length === 0 || selectedYear.length === availableYears.length) {
      return monthIndex > currentMonth;
    }
    
    // If all selected years are in the future, month is definitely in future
    if (selectedYear.every(y => y > currentYear)) return true;
    
    // If all selected years are in the past, month is not in future
    if (selectedYear.every(y => y < currentYear)) return false;
    
    // If current year is selected, check the month
    if (selectedYear.includes(currentYear)) {
      return monthIndex > currentMonth;
    }
    
    return false;
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        marginTop: '8px',
      },
    },
    anchorOrigin: {
      vertical: 'bottom' as const,
      horizontal: 'left' as const,
    },
    transformOrigin: {
      vertical: 'top' as const,
      horizontal: 'left' as const,
    },
    disableScrollLock: true,
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      // background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)',
      p: { xs: 2, md: 3 },
      width: '100%'
    }}>
      {/* Premium Header Section */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 3,
        p: { xs: 2, md: 3 },
        bgcolor: 'white',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        // background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 6px 30px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 4,
            height: 40,
            bgcolor: '#000000',
            borderRadius: 2,
            boxShadow: '0 0 20px rgba(25, 118, 210, 0.4)'
          }} />
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              color: '#000000',
              fontSize: { xs: '0.95rem', md: '1.4rem' },
              letterSpacing: '-0.5px'
            }}
          >
            Leadership Reports
          </Typography>
        </Box>
        <Tooltip 
          title={exportAllLoading ? "Exporting all charts..." : "Export All Charts and Data"}
          arrow
          placement="left"
        >
          <IconButton
            onClick={handleExportMenuClick}
            disabled={exportAllLoading || loading}
            sx={{
              background: exportAllLoading ? 'grey.500' :'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
              color: 'white',
              width: 48,
              height: 48,
              borderRadius: 2,
              // boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                bgcolor: exportAllLoading ? 'grey.500' : 'primary.dark',
                // boxShadow: '0 6px 20px rgba(25, 118, 210, 0.6)',
                transform: 'translateY(-2px) scale(1.05)'
              },
              '&:active': {
                transform: 'translateY(0) scale(0.98)'
              },
              '&:disabled': {
                bgcolor: 'grey.400',
                color: 'grey.600',
                boxShadow: 'none'
              }
            }}
          >
            {exportAllLoading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={exportMenuAnchorEl}
          open={Boolean(exportMenuAnchorEl)}
          onClose={handleExportMenuClose}
          disableScrollLock={true}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              mt: 1,
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                borderRadius: 1,
                mx: 1,
                my: 0.5,
                fontWeight: 500,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: '#ffdcd8',
                  color: 'black',
                  transform: 'translateX(4px)'
                }
              }
            }
          }}
        >
          <MenuItem onClick={handleExportToPDF} disabled={exportAllLoading || loading}>
            Export as PDF
          </MenuItem>
          <MenuItem onClick={handleExportToExcel} disabled={exportAllLoading || loading}>
            Export as Excel
          </MenuItem>
        </Menu>
      </Box>

      {/* Premium Filters Section */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 2, md: 2.5 },
          mb: 3,
          borderRadius: 3,
          bgcolor: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 6px 30px rgba(0,0,0,0.12)'
          }
        }}
      >
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: 2, 
            fontWeight: 600, 
            color: '#000000',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.75rem'
          }}
        >
          Filter Options
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={4} md={2}>
            <FormControl 
              fullWidth 
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  },
                  '&.Mui-focused': {
                    // boxShadow: '0 4px 12px rgba(240, 126, 34, 0.87)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5E4F'
                  },
                  '&.Mui-focused .MuiSelect-select': {
                    color: '#FF5E4F'
                  }
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#FF5E4F'
                }
              }}
            >
              <InputLabel>DU</InputLabel>
              <Select
                multiple
                value={selectedDU}
                onChange={handleDUChange}
                input={<OutlinedInput label="DU" />}
                renderValue={(selected) => {
                  if (!selected || (Array.isArray(selected) && selected.length === 0)) return "";
                  const sel = selected as string[];
                  const specificOptions = duOptions.filter(o => o !== "All");
                  if (sel.includes("All") || sel.length === specificOptions.length) return "All";
                  return sel.join(", ");
                }}
                label="DU"
                MenuProps={MenuProps}
              >
                {duOptions.map((du) => (
                  <MenuItem key={du} value={du}>
                    <Checkbox checked={du === "All" ? selectedDU.includes("All") : (selectedDU.includes("All") || selectedDU.indexOf(du) > -1)} sx={{ color: '#FF5E4F', '&.Mui-checked': { color: '#FF5E4F' } }} />
                    <ListItemText primary={du} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl 
              fullWidth 
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  },
                  '&.Mui-focused': {
                  //  boxShadow: '0 4px 12px rgba(240, 126, 34, 0.87)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5E4F'
                  },
                  '&.Mui-focused .MuiSelect-select': {
                    color: '#FF5E4F'
                  }
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#FF5E4F'
                }
              }}
            >
              <InputLabel>Account</InputLabel>
              <Select
                multiple
                value={selectedAccount}
                onChange={handleAccountChange}
                input={<OutlinedInput label="Account" />}
                renderValue={(selected) => {
                  if (!selected || (Array.isArray(selected) && selected.length === 0)) return "";
                  const sel = selected as string[];
                  const specificOptions = accountOptions.filter(o => o !== "All");
                  if (sel.includes("All") || sel.length === specificOptions.length) return "All";
                  return sel.join(", ");
                }}
                label="Account"
                MenuProps={MenuProps}
              >
                {accountOptions.map((acc) => (
                  <MenuItem key={acc} value={acc}>
                    <Checkbox checked={acc === "All" ? selectedAccount.includes("All") : (selectedAccount.includes("All") || selectedAccount.indexOf(acc) > -1)} sx={{ color: '#FF5E4F', '&.Mui-checked': { color: '#FF5E4F' } }} />
                    <ListItemText primary={acc} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl 
              fullWidth 
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  },
                  '&.Mui-focused': {
                    // boxShadow: '0 4px 12px rgba(240, 126, 34, 0.87)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5E4F'
                  },
                  '&.Mui-focused .MuiSelect-select': {
                    color: '#FF5E4F'
                  }
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#FF5E4F'
                }
              }}
            >
              <InputLabel>PU</InputLabel>
              <Select
                multiple
                value={selectedPU}
                onChange={handlePUChange}
                input={<OutlinedInput label="PU" />}
                renderValue={(selected) => {
                  if (!selected || (Array.isArray(selected) && selected.length === 0)) return "";
                  const sel = selected as string[];
                  const specificOptions = puOptions.filter(o => o !== "All");
                  if (sel.includes("All") || sel.length === specificOptions.length) return "All";
                  return sel.join(", ");
                }}
                label="PU"
                MenuProps={MenuProps}
              >
                {puOptions.map((pu) => (
                  <MenuItem key={pu} value={pu}>
                    <Checkbox checked={pu === "All" ? selectedPU.includes("All") : (selectedPU.includes("All") || selectedPU.indexOf(pu) > -1)} sx={{ color: '#FF5E4F', '&.Mui-checked': { color: '#FF5E4F' } }} />
                    <ListItemText primary={pu} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl 
              fullWidth 
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  },
                  '&.Mui-focused': {
                    // boxShadow: '0 4px 12px rgba(240, 126, 34, 0.87)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5E4F'
                  },
                  '&.Mui-focused .MuiSelect-select': {
                    color: '#FF5E4F'
                  }
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#FF5E4F'
                }
              }}
            >
              <InputLabel>Year</InputLabel>
              <Select
                multiple
                value={allYearsSelected ? ["All" as any, ...selectedYear] : selectedYear}
                onChange={handleYearChange}
                input={<OutlinedInput label="Year" />}
                renderValue={(selected) => {
                  if (!selected || (Array.isArray(selected) && selected.length === 0)) return "";
                  const sel = selected as (number | string)[];
                  const numericYears = sel.filter(v => typeof v === 'number') as number[];
                  if (allYearsSelected || numericYears.length === availableYears.length) return "All";
                  return numericYears.sort((a, b) => b - a).join(", ");
                }}
                label="Year"
                MenuProps={MenuProps}
              >
                <MenuItem key="All" value="All">
                  <Checkbox checked={allYearsSelected} sx={{ color: '#FF5E4F', '&.Mui-checked': { color: '#FF5E4F' } }} />
                  <ListItemText primary="All" />
                </MenuItem>
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    <Checkbox checked={allYearsSelected || selectedYear.indexOf(year) > -1} sx={{ color: '#FF5E4F', '&.Mui-checked': { color: '#FF5E4F' } }} />
                    <ListItemText primary={year} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl 
              fullWidth 
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(240, 126, 34, 0.87)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5E4F'
                  },
                  '&.Mui-focused .MuiSelect-select': {
                    color: '#FF5E4F'
                  }
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#FF5E4F'
                }
              }}
            >
              <InputLabel>Quarters</InputLabel>
              <Select
                multiple
                value={allQuartersSelected ? ["All"] : selectedQuarters}
                onChange={handleQuarterChange}
                input={<OutlinedInput label="Quarters" />}
                renderValue={(selected) => {
                  if (!selected || (Array.isArray(selected) && selected.length === 0)) return "";
                  const sel = selected as string[];
                  const allQuarterOptions = ["Q1", "Q2", "Q3", "Q4"];
                  if (sel.includes("All") || sel.length === allQuarterOptions.length) return "All";
                  return sel.join(", ");
                }}
                MenuProps={MenuProps}
              >
                <MenuItem key="All" value="All">
                  <Checkbox checked={allQuartersSelected} sx={{ color: '#FF5E4F', '&.Mui-checked': { color: '#FF5E4F' } }} />
                  <ListItemText primary="All" />
                </MenuItem>
                {["Q1", "Q2", "Q3", "Q4"].map((q) => (
                  <MenuItem key={q} value={q} disabled={isQuarterInFuture(q)}>
                    <Checkbox checked={allQuartersSelected || selectedQuarters.indexOf(q) > -1} sx={{ color: '#FF5E4F', '&.Mui-checked': { color: '#FF5E4F' } }} />
                    <ListItemText primary={q} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl 
              fullWidth 
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(240, 126, 34, 0.87)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF5E4F'
                  },
                  '&.Mui-focused .MuiSelect-select': {
                    color: '#FF5E4F'
                  }
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#FF5E4F'
                }
              }}
            >
              <InputLabel>Months</InputLabel>
              <Select
                multiple
                value={allMonthsSelected ? ["All"] : selectedMonths}
                onChange={handleMonthChange}
                input={<OutlinedInput label="Months" />}
                renderValue={(selected) => {
                  if (!selected || (Array.isArray(selected) && selected.length === 0)) return "";
                  const sel = selected as string[];
                  const monthOptions = (selectedQuarters.length === 0
                    ? monthsOrder
                    : monthsOrder.filter((m) =>
                        selectedQuarters.some((q) => quarterMonthMap[q].includes(m))
                      )
                  );
                  if (sel.includes("All") || sel.length === monthOptions.length) return "All";
                  return sel.join(", ");
                }}
                MenuProps={MenuProps}
              >
                <MenuItem key="All" value="All">
                  <Checkbox checked={allMonthsSelected} sx={{ color: '#FF5E4F', '&.Mui-checked': { color: '#FF5E4F' } }} />
                  <ListItemText primary="All" />
                </MenuItem>
                {(selectedQuarters.length === 0
                  ? monthsOrder
                  : monthsOrder.filter((m) =>
                    selectedQuarters.some((q) =>
                      quarterMonthMap[q].includes(m)
                    )
                  )
                ).map((month) => (
                  <MenuItem
                    key={month}
                    value={month}
                    disabled={isMonthInFuture(month)}
                  >
                    <Checkbox checked={allMonthsSelected || selectedMonths.indexOf(month) > -1} sx={{ color: '#FF5E4F', '&.Mui-checked': { color: '#FF5E4F' } }} />
                    <ListItemText primary={month} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 1, md: 2 }, position: "relative", boxShadow: 1 }}>
            <ChartExportMenu
              chartRef={monthlyChartRef}
              chartData={monthlyChartData}
              chartTitle="Monthly Assessment Status"
              loading={monthlyChartLoading}
              filterInfo={{
                Year: allYearsSelected ? "All" : selectedYear.sort((a, b) => b - a).join(", "),
                DU: selectedDU,
                Account: selectedAccount,
                PU: selectedPU,
                Quarters: allQuartersSelected ? "All" : selectedQuarters,
                Months: allMonthsSelected ? "All" : selectedMonths,
              }}
            />
            <div ref={monthlyChartRef}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 2,
                gap: 2
              }}>
                <Box sx={{ 
                  height: 3, 
                  width: 40, 
                  bgcolor: '#000000',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(1, 32, 99, 0.28)'
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    color: '#000000',
                    letterSpacing: '-0.3px'
                  }}
                >
                  Monthly Assessment Status
                </Typography>
                <Box sx={{ 
                  height: 3, 
                  width: 40, 
                  bgcolor: '#000000',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(1, 32, 99, 0.28)'
                }} />
              </Box>

              {loading || monthlyChartLoading ? (
                <ChartSkeletonLoader height={320} />
              ) : (
                <>
                  <TotalCountsRow
                    labels={monthlyChartData.labels}
                    totals={getMonthlyTotals(
                      filteredMonthlyAssessmentStatus,
                      filteredMonthKeys
                    )}
                    paddingLeft="69px"
                    paddingRight="100px"
                    itemWidth={200}
                    maxItemWidth="60px"
                    fontSize="0.65rem"
                    containerRef={monthlyChartRef}
                    chartInstanceRef={monthlyChartInstanceRef}
                  />
                  <Bar
                    data={monthlyChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: { position: "right" as const },
                        title: { display: false },
                        tooltip: {
                          mode: "index" as const,
                          intersect: false,
                          callbacks: {
                            afterTitle: (context: any) => {
                              if (!context || !context.length) return "";
                              const label = context[0].label;
                              const total = getMonthlyTotals(
                                filteredMonthlyAssessmentStatus,
                                filteredMonthKeys
                              )[label] || 0;
                              return `Total Projects: ${total}`;
                            },
                            label: (context: any) => {
                              const datasetLabel = context.dataset.label || '';
                              const value = context.parsed.y;
                              if (datasetLabel === 'N/A') {
                                return `NoRAG: ${value}`;
                              }
                              return `${datasetLabel}: ${value}`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          ticks: {
                            precision: 0,
                            callback: function (value: any) {
                              if (Math.floor(value) === value) {
                                return value;
                              }
                            }
                          }
                        }
                      },
                    }}
                    height={80}
                    ref={monthlyChartInstanceRef}
                  />
                </>
              )}
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, md: 3 },
              position: "relative",
              borderRadius: 3,
              bgcolor: 'white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.3s ease',
              overflow: 'hidden',
              '&:hover': {
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <ChartExportMenu
              chartRef={findingsChartRef}
              chartData={findingsChartData}
              chartTitle="Findings by Status per Month"
              loading={findingsChartLoading}
              filterInfo={{
                Year: allYearsSelected ? "All" : selectedYear.sort((a, b) => b - a).join(", "),
                DU: selectedDU,

                Account: selectedAccount,
                PU: selectedPU,
                Quarters: allQuartersSelected ? "All" : selectedQuarters,
                Months: allMonthsSelected ? "All" : selectedMonths,
              }}
            />
            <div ref={findingsChartRef}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 2,
                gap: 2
              }}>
                <Box sx={{ 
                  height: 3, 
                  width: 40, 
                  bgcolor: '#000000',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(1, 32, 99, 0.28)'
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    color: '#000000',
                    letterSpacing: '-0.3px'
                  }}
                >
                  Findings by Status per Month
                </Typography>
                <Box sx={{ 
                  height: 3, 
                  width: 40, 
                  bgcolor: '#000000',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(1, 32, 99, 0.28)'
                }} />
              </Box>

              {loading || findingsChartLoading ? (
                <ChartSkeletonLoader height={320} />
              ) : (
                <>
                  <TotalCountsRow
                    labels={findingsChartData.labels}
                    totals={getMonthlyTotals(
                      filteredMonthlyObservationStatus,
                      filteredMonthKeys
                    )}
                    title="Total Findings"
                    paddingLeft="68px"
                    paddingRight="120px"
                    itemWidth={200}
                    maxItemWidth="60px"
                    fontSize="0.65rem"
                    containerRef={findingsChartRef}
                    chartInstanceRef={findingsChartInstanceRef}
                  />
                  <Bar
                    data={findingsChartData}
                    options={{
                      ...chartOptions(
                        "",
                        getMonthlyTotals(filteredMonthlyObservationStatus, filteredMonthKeys),
                        false
                      ),
                      plugins: {
                        ...chartOptions("", {}, false).plugins,
                        title: { display: false },
                      },
                      maintainAspectRatio: true,
                      responsive: true,
                    }}
                    height={80}
                    ref={findingsChartInstanceRef}
                  />
                </>
              )}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, md: 3 },
              position: "relative",
              borderRadius: 3,
              bgcolor: 'white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.3s ease',
              overflow: 'hidden',
              '&:hover': {
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <ChartExportMenu
              chartRef={quarterlyAssessmentVsFindingsChartRef}
              chartData={quarterlyAssessmentVsFindingsChartData}
              chartTitle="Quarterly Assessment Status vs Findings Status"
              loading={quarterlyChartLoading}
              filterInfo={{
                Year: allYearsSelected ? "All" : selectedYear.sort((a, b) => b - a).join(", "),
                DU: selectedDU,
                Account: selectedAccount,
                PU: selectedPU,
              }}
            />
            <div ref={quarterlyAssessmentVsFindingsChartRef}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 2,
                gap: 2
              }}>
                <Box sx={{ 
                  height: 3, 
                  width: 40, 
                  bgcolor: '#000000',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(1, 32, 99, 0.28)'
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    color: '#000000',
                    letterSpacing: '-0.3px'
                  }}
                >
                  Quarterly Assessment Status vs Findings Status
                </Typography>
                <Box sx={{ 
                  height: 3, 
                  width: 40, 
                  bgcolor: '#000000',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(1, 32, 99, 0.28)'
                }} />
              </Box>

              {loading || quarterlyChartLoading ? (
                <ChartSkeletonLoader height={320} />
              ) : (
                <>
                  <TotalCountsRow
                    labels={getFilteredQuarterLabels()}
                    totals={getQuarterTotals()}
                    title="Total Assessments"
                    paddingLeft="120px"
                    paddingRight="120px"
                    itemWidth={200}
                    maxItemWidth="100px"
                    fontSize="0.65rem"
                    containerRef={quarterlyAssessmentVsFindingsChartRef}
                    chartInstanceRef={quarterlyAssessmentVsFindingsChartInstanceRef}
                  />
                  <Bar
                    data={quarterlyAssessmentVsFindingsChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: { position: "top" },
                        title: { display: false },
                        tooltip: {
                          mode: "index" as const,
                          intersect: false,
                          callbacks: {
                            afterTitle: (context: any) => {
                              const label = context[0].label;
                              const totals = getQuarterTotals();
                              const total = totals[label] || 0;
                              return `Total Assessments: ${total}`;
                            },
                            label: (context: any) => {
                              const datasetLabel = context.dataset.label || '';
                              const value = context.parsed.y;
                              if (datasetLabel === 'NoRAG') {
                                return `NoRAG: ${value}`;
                              }
                              return `${datasetLabel}: ${value}`;
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          stacked: false,
                          title: { display: true, text: "Quarter" },
                        },
                        y: {
                          stacked: false,
                          beginAtZero: true,
                          title: { display: true, text: "Count" },
                          ticks: {
                            callback: (v: any) => (Number.isInteger(v) ? v : ""),
                            stepSize: 1,
                          },
                          suggestedMax: undefined,
                        },
                      },
                    }}
                    height={80}
                    ref={quarterlyAssessmentVsFindingsChartInstanceRef}
                  />
                </>
              )}
            </div>
          </Paper>

        </Grid>

        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, md: 3 },
              position: "relative",
              borderRadius: 3,
              bgcolor: 'white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.3s ease',
              overflow: 'hidden',
              '&:hover': {
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <ChartExportMenu
              chartRef={assessmentReportChartRef}
              chartData={assessmentReportChartData}
              chartTitle="Assessment Report"
              loading={loading}
              filterInfo={{
                Year: allYearsSelected ? "All" : selectedYear.sort((a, b) => b - a).join(", "),
                DU: selectedDU,
                Account: selectedAccount,
                PU: selectedPU,
                Quarters: allQuartersSelected ? "All" : selectedQuarters,
                Months: allMonthsSelected ? "All" : selectedMonths,
              }}
            />
            <div ref={assessmentReportChartRef}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 2,
                gap: 2
              }}>
                <Box sx={{ 
                  height: 3, 
                  width: 40, 
                  bgcolor: '#000000',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(1, 32, 99, 0.28)'
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    color: '#000000',
                    letterSpacing: '-0.3px'
                  }}
                >
                  Assessment Report
                </Typography>
                <Box sx={{ 
                  height: 3, 
                  width: 40, 
                  bgcolor: '#000000',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(1, 32, 99, 0.28)'
                }} />
              </Box>

              {loading || monthlyChartLoading ? (
                <ChartSkeletonLoader height={320} />
              ) : (
                <>
                  <TotalCountsRow 
                    labels={assessmentReportChartData.labels}
                    totals={getMonthlyTotals(
                      assessmentReportData,
                      filteredMonthKeys
                    )}
                    title="Total Assessments"
                    paddingLeft="68px"
                    paddingRight="120px"
                    itemWidth={200}
                    maxItemWidth="60px"
                    fontSize="0.65rem"
                    containerRef={assessmentReportChartRef}
                    chartInstanceRef={assessmentReportChartInstanceRef}
                  />
                  <Bar
                    data={assessmentReportChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: { position: "right" as const },
                        title: { display: false },
                        tooltip: {
                          mode: "index" as const,
                          intersect: false,
                          callbacks: {
                            afterTitle: (context: any) => {
                              if (!context || !context.length) return "";
                              const label = context[0].label;
                              const total = getMonthlyTotals(
                                assessmentReportData,
                                filteredMonthKeys
                              )[label] || 0;
                              return `Total Assessments: ${total}`;
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          stacked: false,
                          title: { display: true, text: "Month-Year" },
                        },
                        y: {
                          stacked: false,
                          beginAtZero: true,
                          title: { display: true, text: "Count" },
                          ticks: {
                            callback: (v: any) => (Number.isInteger(v) ? v : ""),
                            stepSize: 1,
                          },
                          suggestedMax: undefined,
                        },
                      },
                    }}
                    height={80}
                    ref={assessmentReportChartInstanceRef}
                  />
                </>
              )}
            </div>
          </Paper>
        </Grid>

      </Grid>
      {!loading && (!filteredProjects || filteredProjects.length === 0) && (
        <Typography align="center" color="textSecondary" sx={{ mt: 4 }}>
          No data available for the selected filters.
        </Typography>
      )}
    </Box>
  );
};

export default QuarterReportCharts;
